import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { canEditModule } from "@/utils/permissions";
import CustomFieldsPanel from "@/pages/ViajeroExternoCustomFields";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe, Plus, Copy, RefreshCw, Pencil, Download, Upload, Loader2, ExternalLink, ChevronDown, ChevronRight, Mail, Archive, Ban, Trash2, ArchiveRestore, Send, CheckCircle2, MailOpen, MousePointerClick, AlertOctagon, Clock, FileSpreadsheet, FileText, Eye, CreditCard } from "lucide-react";
import { ModuleHeaderIcon } from "@/components/ModuleHeader";
import { DesignEditor, SendLinkDialog } from "@/components/ViajeroExtDesign";
import InvitationsDialog from "@/components/InvitationsDialog";
import ClientCombobox from "@/components/ClientCombobox";
import DocumentPreviewDialog from "@/components/DocumentPreviewDialog";
import RichTextEditor from "@/components/RichTextEditor";
import { PagosTab, PagoServiciosPanel } from "@/pages/ViajeroExternoPagos";

const ESTATUS = ["Nuevo", "En revisión", "Completo", "Confirmado", "Cancelado"];
const EST_CLS = { "Nuevo": "bg-blue-100 text-blue-700", "En revisión": "bg-amber-100 text-amber-700", "Completo": "bg-emerald-100 text-emerald-700", "Confirmado": "bg-cyan-100 text-cyan-700", "Cancelado": "bg-red-100 text-red-700" };
const DOC_CLS = { vigente: "bg-emerald-100 text-emerald-700", por_vencer: "bg-amber-100 text-amber-700", vencido: "bg-red-100 text-red-700", sin_fecha: "bg-slate-100 text-slate-600" };
const CONSENTS = ["cPrivacidad", "cDatos"];

const fieldActive = (fc, sk, fk) => {
  if (CONSENTS.includes(fk)) return true;
  const sec = (fc || {})[sk];
  if (!sec) return true;
  if (sec.on === false && sk !== "consentimientos") return false;
  return (sec.f || {})[fk] !== false;
};

const FieldsConfigurator = ({ catalogo, fc, setFc }) => {
  const [open, setOpen] = useState({});

  // Orden efectivo de secciones: fc._section_order (si existe) + resto en orden original
  const sectionsInOrder = useMemo(() => {
    const base = catalogo.sections;
    const custom = (fc?._section_order || []);
    const known = new Set(base.map((s) => s.key));
    const orderIdx = new Map(custom.filter((k) => known.has(k)).map((k, i) => [k, i]));
    return [...base].sort((a, b) => {
      const ai = orderIdx.has(a.key) ? orderIdx.get(a.key) : 10000 + base.indexOf(a);
      const bi = orderIdx.has(b.key) ? orderIdx.get(b.key) : 10000 + base.indexOf(b);
      return ai - bi;
    });
  }, [catalogo, fc?._section_order]);

  // Orden efectivo de campos dentro de una sección
  const fieldsInOrder = (s) => {
    const base = s.fields;
    const custom = fc?.[s.key]?.order || [];
    const known = new Set(base.map((f) => f.k));
    const orderIdx = new Map(custom.filter((k) => known.has(k)).map((k, i) => [k, i]));
    return [...base].sort((a, b) => {
      const ai = orderIdx.has(a.k) ? orderIdx.get(a.k) : 10000 + base.indexOf(a);
      const bi = orderIdx.has(b.k) ? orderIdx.get(b.k) : 10000 + base.indexOf(b);
      return ai - bi;
    });
  };

  const moveSection = (key, dir) => {
    const currentOrder = sectionsInOrder.map((s) => s.key);
    const idx = currentOrder.indexOf(key);
    const tgt = idx + dir;
    if (tgt < 0 || tgt >= currentOrder.length) return;
    const arr = [...currentOrder];
    [arr[idx], arr[tgt]] = [arr[tgt], arr[idx]];
    const next = JSON.parse(JSON.stringify(fc || {}));
    next._section_order = arr;
    setFc(next);
  };

  const moveField = (sk, fk, dir) => {
    const sec = catalogo.sections.find((s) => s.key === sk);
    if (!sec) return;
    const currentOrder = fieldsInOrder(sec).map((f) => f.k);
    const idx = currentOrder.indexOf(fk);
    const tgt = idx + dir;
    if (tgt < 0 || tgt >= currentOrder.length) return;
    const arr = [...currentOrder];
    [arr[idx], arr[tgt]] = [arr[tgt], arr[idx]];
    const next = JSON.parse(JSON.stringify(fc || {}));
    next[sk] = next[sk] || { on: true, f: {} };
    next[sk].order = arr;
    setFc(next);
  };

  const resetOrder = () => {
    if (!window.confirm("¿Restaurar el orden original de secciones y preguntas?")) return;
    const next = JSON.parse(JSON.stringify(fc || {}));
    delete next._section_order;
    Object.keys(next).forEach((k) => { if (next[k] && typeof next[k] === "object" && "order" in next[k]) delete next[k].order; });
    setFc(next);
  };

  const fieldReq = (sk, f) => {
    if (CONSENTS.includes(f.k)) return true;
    const r = fc?.[sk]?.r || {};
    return f.k in r ? !!r[f.k] : !!f.req;
  };
  const setReq = (sk, f, req) => {
    const next = JSON.parse(JSON.stringify(fc || {}));
    next[sk] = next[sk] || { on: true, f: {} };
    next[sk].r = next[sk].r || {};
    if (req === !!f.req) delete next[sk].r[f.k]; else next[sk].r[f.k] = req;
    setFc(next);
  };
  const setField = (sk, fk, on) => {
    const next = JSON.parse(JSON.stringify(fc || {}));
    next[sk] = next[sk] || { on: true, f: {} };
    next[sk].f = next[sk].f || {};
    if (on) delete next[sk].f[fk]; else next[sk].f[fk] = false;
    const sec = catalogo.sections.find((s) => s.key === sk);
    const actives = sec.fields.filter((f) => !CONSENTS.includes(f.k) && (next[sk].f[f.k] !== false)).length;
    next[sk].on = sk === "consentimientos" ? true : actives > 0;
    setFc(next);
  };
  const setSection = (sk, on) => {
    if (sk === "consentimientos" && !on) return;
    const next = JSON.parse(JSON.stringify(fc || {}));
    const sec = catalogo.sections.find((s) => s.key === sk);
    next[sk] = { on, f: {} };
    if (!on) sec.fields.forEach((f) => { if (!CONSENTS.includes(f.k)) next[sk].f[f.k] = false; });
    setFc(next);
  };
  const setAll = (on) => {
    const next = { ...(fc || {}) };
    // preservar orden custom
    catalogo.sections.forEach((s) => {
      next[s.key] = { ...(next[s.key] || {}), on: s.key === "consentimientos" ? true : on, f: {} };
      if (!on) s.fields.forEach((f) => { if (!CONSENTS.includes(f.k)) next[s.key].f[f.k] = false; });
    });
    setFc(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="font-semibold">Preguntas del formulario</Label>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" onClick={resetOrder} data-testid="cfg-reset-orden">Restaurar orden</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setAll(true)} data-testid="cfg-activar-todo">Activar todo</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setAll(false)} data-testid="cfg-desactivar-todo">Desactivar todo</Button>
        </div>
      </div>
      {/* Encabezado de columnas para claridad */}
      <div className="flex items-center gap-2 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 border border-slate-200 bg-slate-50 rounded-md">
        <div className="w-5 shrink-0" />
        <div className="w-16 shrink-0 text-center">Orig&nbsp;→&nbsp;Nvo</div>
        <div className="w-4 shrink-0" />
        <div className="flex-1">
          Pregunta <span className="text-red-500 normal-case text-sm">*</span><span className="text-slate-400 font-medium normal-case text-[10px] ml-1">obligatoria</span>
        </div>
        <div className="w-20 text-right pr-1">Estatus</div>
      </div>
      {sectionsInOrder.map((s, idx) => {
        const total = s.fields.length;
        const activos = s.fields.filter((f) => fieldActive(fc, s.key, f.k)).length;
        const secOn = s.key === "consentimientos" ? true : (fc?.[s.key]?.on !== false && activos > 0);
        const origIdx = catalogo.sections.findIndex((x) => x.key === s.key);
        const orderChanged = idx !== origIdx;
        return (
          <div key={s.key} className="border border-slate-200 rounded-lg" data-testid={`cfg-sec-${s.key}`}>
            <div className="flex items-center gap-2 px-3 py-2">
              <div className="flex flex-col gap-0.5 w-5 shrink-0 items-center">
                <button type="button" disabled={idx === 0} onClick={() => moveSection(s.key, -1)}
                  className="text-slate-400 hover:text-slate-700 disabled:opacity-20 leading-none text-xs" title="Subir sección" data-testid={`cfg-sec-up-${s.key}`}>▲</button>
                <button type="button" disabled={idx === sectionsInOrder.length - 1} onClick={() => moveSection(s.key, 1)}
                  className="text-slate-400 hover:text-slate-700 disabled:opacity-20 leading-none text-xs" title="Bajar sección" data-testid={`cfg-sec-down-${s.key}`}>▼</button>
              </div>
              <span className={`w-16 shrink-0 text-center text-[10px] font-semibold px-1 py-0.5 rounded ${orderChanged ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}
                title={`Orden original ${origIdx + 1} · Orden personalizado ${idx + 1}`}>
                {origIdx + 1} → {idx + 1}
              </span>
              <Checkbox checked={secOn} disabled={s.key === "consentimientos"} onCheckedChange={(v) => setSection(s.key, !!v)} data-testid={`cfg-sec-check-${s.key}`} />
              <button type="button" className="flex-1 flex items-center justify-between text-left" onClick={() => setOpen((o) => ({ ...o, [s.key]: !o[s.key] }))}>
                <span className="text-sm font-semibold text-slate-700">{s.es || s.title || s.label}</span>
                <span className="flex items-center gap-2 text-xs text-slate-400">{activos}/{total} activos <ChevronDown className={`w-4 h-4 transition-transform ${open[s.key] ? "rotate-180" : ""}`} /></span>
              </button>
            </div>
            {open[s.key] && (
              <div className="border-t border-slate-100 px-4 py-2 space-y-1">
                {fieldsInOrder(s).map((f, fIdx) => {
                  const active = fieldActive(fc, s.key, f.k);
                  const consent = CONSENTS.includes(f.k);
                  const req = fieldReq(s.key, f);
                  const fOrig = s.fields.findIndex((x) => x.k === f.k);
                  const fChanged = fIdx !== fOrig;
                  const totalFields = s.fields.length;
                  return (
                    <div key={f.k} className="flex items-center gap-2 text-sm text-slate-600 py-0.5">
                      <div className="flex flex-col gap-0.5 w-5 shrink-0 items-center text-[10px]">
                        <button type="button" disabled={fIdx === 0 || consent} onClick={() => moveField(s.key, f.k, -1)}
                          className="text-slate-300 hover:text-slate-700 disabled:opacity-20 leading-none" title="Subir" data-testid={`cfg-f-up-${f.k}`}>▲</button>
                        <button type="button" disabled={fIdx === totalFields - 1 || consent} onClick={() => moveField(s.key, f.k, 1)}
                          className="text-slate-300 hover:text-slate-700 disabled:opacity-20 leading-none" title="Bajar" data-testid={`cfg-f-down-${f.k}`}>▼</button>
                      </div>
                      <span className={`w-16 shrink-0 text-center text-[10px] font-semibold px-1 py-0.5 rounded ${fChanged ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400"}`}
                        title={`Orden original ${fOrig + 1} · Orden personalizado ${fIdx + 1}`}>
                        {fOrig + 1} → {fIdx + 1}
                      </span>
                      <Checkbox checked={active} disabled={consent} onCheckedChange={(v) => setField(s.key, f.k, !!v)} data-testid={`cfg-f-${f.k}`} />
                      <span className="truncate flex items-center gap-1">
                        {f.col}
                        {active && (
                          <button type="button" disabled={consent}
                            onClick={() => setReq(s.key, f, !req)}
                            title={consent ? "Siempre obligatoria" : req ? "Obligatoria — clic para hacerla opcional" : "Opcional — clic para hacerla obligatoria"}
                            className={`text-base font-black leading-none -mt-0.5 transition-colors ${req ? "text-red-500" : "text-slate-300 hover:text-red-300"} ${consent ? "cursor-default" : "cursor-pointer"}`}
                            data-state={req ? "checked" : "unchecked"} data-testid={`cfg-req-${f.k}`}>*</button>
                        )}
                      </span>
                      <div className="flex-1" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default function ViajeroExterno() {
  const { user } = useAuth();
  const canEditModuleFlag = canEditModule(user, "traveldesk_perfil_viajero_externo") || canEditModule(user, "hospitality_perfil_viajero_externo");
  const canEdit = canEditModuleFlag; // usa este alias local para las tabs
  // Edición del perfil registrado (contenido) — solo admin/supervisor
  const canEditPerfil = user?.role === "admin" || user?.role === "supervisor";
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [catalogo, setCatalogo] = useState(null);
  const [perfiles, setPerfiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projDialog, setProjDialog] = useState(null);
  const [sendLink, setSendLink] = useState(null);
  const [invitationsFor, setInvitationsFor] = useState(null); // proyecto para el tablero de invitaciones
  const [detail, setDetail] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({ project_id: "", estatus: "", q: "", desde: "", hasta: "", subtipo_id: "" });
  const [usersList, setUsersList] = useState([]);
  const [clientOpts, setClientOpts] = useState([]);
  const [manifOpen, setManifOpen] = useState(false);
  const [manifSubtipoOpen, setManifSubtipoOpen] = useState(false); // Paso 1: elegir subtipos
  const [manifSubtipoIds, setManifSubtipoIds] = useState({}); // { subtipo_id: bool }
  const [manifSel, setManifSel] = useState({ folio: true });
  const [manifConfirmFiles, setManifConfirmFiles] = useState(false);
  const [manifDownloading, setManifDownloading] = useState(null); // null | "excel" | "zip"
  const [manifProgress, setManifProgress] = useState(0);
  const [exportingPerfil, setExportingPerfil] = useState(null); // null | "xlsx" | "pdf"
  const [preview, setPreview] = useState(null); // vista previa de documento: { url, filename, label }

  const loadPerfiles = useCallback(async (f = filters) => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    const [pRes, sRes] = await Promise.all([
      axios.get(`${API}/viajeros-ext/perfiles`, { params, withCredentials: true }),
      axios.get(`${API}/viajeros-ext/stats`, { params: f.project_id ? { project_id: f.project_id } : {}, withCredentials: true }),
    ]);
    setPerfiles(pRes.data || []);
    setStats(sRes.data);
  }, [filters]);

  const loadCatalogo = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/viajeros-ext/catalogo`, { withCredentials: true });
      setCatalogo(r.data);
    } catch (e) { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const [prRes, cRes] = await Promise.all([
        axios.get(`${API}/viajeros-ext/projects`, { withCredentials: true }),
        axios.get(`${API}/viajeros-ext/catalogo`, { withCredentials: true }),
      ]);
      setProjects(prRes.data || []);
      setCatalogo(cRes.data);
      axios.get(`${API}/users-list`, { withCredentials: true }).then((r) => setUsersList(r.data || [])).catch(() => {});
      await loadPerfiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const pid = searchParams.get("perfil");
    if (pid) axios.get(`${API}/viajeros-ext/perfiles/${pid}`, { withCredentials: true }).then((r) => { setDetail(r.data); setEdits({}); }).catch(() => {});
  }, [searchParams]);

  // Contador para forzar refresco cruzado entre pestaña Perfiles ↔ pestaña Pagos
  const [pagosRefreshKey, setPagosRefreshKey] = useState(0);

  const openPerfilDetail = async (pid) => {
    try {
      const r = await axios.get(`${API}/viajeros-ext/perfiles/${pid}`, { withCredentials: true });
      setDetail(r.data);
      setEdits({});
    } catch (e) {
      toast.error("No se pudo cargar el perfil");
    }
  };

  const publicUrl = (p) => `${window.location.origin}/viajero/${p.slug}`;
  const copyLink = (p) => { navigator.clipboard.writeText(publicUrl(p)); toast.success("Link copiado"); };

  const [newProjWizard, setNewProjWizard] = useState(null); // { nombre, subtipo }
  const openNewProject = () => setNewProjWizard({ nombre: "", subtipo: "General" });
  const startNewProjectFromWizard = () => {
    if (!newProjWizard?.nombre?.trim()) return toast.error("El nombre del proyecto es obligatorio");
    if (!newProjWizard?.subtipo?.trim()) return toast.error("El nombre del primer subtipo es obligatorio");
    setSubtipoNewName(newProjWizard.subtipo.trim());
    setProjDialog({
      nombre: newProjWizard.nombre.trim(), cliente_nombre: "", activo: true,
      idiomas: { en: false, fr: false }, branding: {}, fields_config: {}, notificar_a: [],
      aviso_privacidad_url: "", mensaje_bienvenida: {},
      pago_servicios_config: { enabled: false, titulo: "Pago de Servicios", titulo_en: "", titulo_fr: "", posicion: 999, tiempo_contacto_valor: 2, tiempo_contacto_unidad: "horas", servicios: [], boton_si: "", boton_si_en: "", boton_si_fr: "", boton_no: "", boton_no_en: "", boton_no_fr: "", texto_no: "", texto_no_en: "", texto_no_fr: "" },
    });
    setNewProjWizard(null);
  };

  const [activeSubtipoIx, setActiveSubtipoIx] = useState(0);
  const [subtipoNewOpen, setSubtipoNewOpen] = useState(false);
  const [subtipoNewName, setSubtipoNewName] = useState("");
  const [linksPopup, setLinksPopup] = useState(null); // { project }

  // Sincroniza estado local del subtipo activo cada vez que se abre el proyecto o cambia el índice
  const activeSubtipo = projDialog?.subtipos?.[activeSubtipoIx] || null;

  const loadSubtipoIntoDialog = (proj, ix) => {
    const st = (proj.subtipos || [])[ix];
    if (!st) return proj;
    return {
      ...proj,
      // Espejos: los inputs existentes editan estos campos que después se envían al subtipo activo
      fields_config: st.fields_config || {},
      pago_servicios_config: st.pago_servicios_config || { enabled: false, titulo: "Pago de Servicios", servicios: [] },
      mensaje_bienvenida: st.mensaje_bienvenida || {},
      titulo_publico_activo: !!st.titulo_publico_activo,
      titulo_publico: st.titulo_publico || "",
    };
  };

  const switchSubtipo = (ix) => {
    if (!projDialog?.subtipos?.[ix]) return;
    // Persistir cambios locales del subtipo actual antes de cambiar
    const cur = projDialog.subtipos[activeSubtipoIx];
    const nextSubtipos = projDialog.subtipos.map((s, i) => i === activeSubtipoIx ? {
      ...s,
      fields_config: projDialog.fields_config || {},
      pago_servicios_config: projDialog.pago_servicios_config || {},
      mensaje_bienvenida: projDialog.mensaje_bienvenida || {},
      titulo_publico_activo: !!projDialog.titulo_publico_activo,
      titulo_publico: projDialog.titulo_publico || "",
    } : s);
    setActiveSubtipoIx(ix);
    setProjDialog(loadSubtipoIntoDialog({ ...projDialog, subtipos: nextSubtipos }, ix));
  };

  const addSubtipo = async () => {
    if (!projDialog?.id) return toast.error("Primero guarda el proyecto");
    const nombre = (subtipoNewName || "").trim();
    if (!nombre) return toast.error("Escribe un nombre para el subtipo");
    if ((projDialog.subtipos || []).length >= 54) return toast.error("Máximo 54 subtipos por proyecto");
    try {
      const r = await axios.post(`${API}/viajeros-ext/projects/${projDialog.id}/subtipos`, { nombre }, { withCredentials: true });
      const nextSubtipos = [...(projDialog.subtipos || []), r.data];
      const newProj = { ...projDialog, subtipos: nextSubtipos };
      setProjDialog(loadSubtipoIntoDialog(newProj, nextSubtipos.length - 1));
      setActiveSubtipoIx(nextSubtipos.length - 1);
      setSubtipoNewName("");
      setSubtipoNewOpen(false);
      toast.success(`Subtipo "${nombre}" creado`);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo crear"); }
  };

  const removeSubtipo = async (subtipo_id) => {
    if (!projDialog?.id) return;
    if (!window.confirm("¿Eliminar este subtipo? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/viajeros-ext/projects/${projDialog.id}/subtipos/${subtipo_id}`, { withCredentials: true });
      const nextSubtipos = (projDialog.subtipos || []).filter((s) => s.id !== subtipo_id);
      const newIx = 0;
      setProjDialog(loadSubtipoIntoDialog({ ...projDialog, subtipos: nextSubtipos }, newIx));
      setActiveSubtipoIx(newIx);
      toast.success("Subtipo eliminado");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar"); }
  };

  const saveProject = async () => {
    if (!projDialog.nombre?.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    const notif = Array.isArray(projDialog.notificar_a) ? projDialog.notificar_a : String(projDialog.notificar_a || "").split(",").map((s) => s.trim()).filter(Boolean);
    try {
      if (projDialog.id) {
        // 1) Actualizar campos del PROYECTO (compartidos)
        const projectPayload = {
          nombre: projDialog.nombre, cliente_id: projDialog.cliente_id, cliente_nombre: projDialog.cliente_nombre,
          cliente_comercial: projDialog.cliente_comercial, activo: projDialog.activo,
          idiomas: projDialog.idiomas, branding: projDialog.branding, notificar_a: notif,
          aviso_privacidad_url: projDialog.aviso_privacidad_url,
          encargado: projDialog.encargado, destinos: projDialog.destinos,
          fecha_inicio: projDialog.fecha_inicio, fecha_fin: projDialog.fecha_fin,
          contacto_nombre: projDialog.contacto_nombre, contacto_telefono: projDialog.contacto_telefono,
        };
        await axios.put(`${API}/viajeros-ext/projects/${projDialog.id}`, projectPayload, { withCredentials: true });
        // 2) Actualizar el SUBTIPO activo con los campos por-subtipo
        if (activeSubtipo?.id) {
          const subtipoPayload = {
            fields_config: projDialog.fields_config || {},
            pago_servicios_config: projDialog.pago_servicios_config || {},
            mensaje_bienvenida: projDialog.mensaje_bienvenida || {},
            titulo_publico_activo: !!projDialog.titulo_publico_activo,
            titulo_publico: projDialog.titulo_publico || "",
          };
          await axios.patch(`${API}/viajeros-ext/projects/${projDialog.id}/subtipos/${activeSubtipo.id}`, subtipoPayload, { withCredentials: true });
        }
      } else {
        const payload = { ...projDialog, notificar_a: notif, subtipo_inicial_nombre: subtipoNewName || "General" };
        await axios.post(`${API}/viajeros-ext/projects`, payload, { withCredentials: true });
      }
      toast.success("Proyecto guardado");
      setProjDialog(null);
      setSubtipoNewName("");
      const r = await axios.get(`${API}/viajeros-ext/projects`, { withCredentials: true });
      setProjects(r.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const isAdmin = user?.role === "admin";
  const canDeletePerfil = user?.role === "admin" || user?.role === "supervisor";
  const [projF, setProjF] = useState({ q: "", status: "activo" });
  const [projAction, setProjAction] = useState(null); // { tipo: 'archivado'|'cancelado'|'activo'|'eliminar', p }
  const [perfProjStatus, setPerfProjStatus] = useState("todos");

  const setProjectStatus = async (p, status) => {
    try {
      await axios.patch(`${API}/viajeros-ext/projects/${p.id}/status`, { status }, { withCredentials: true });
      toast.success(status === "activo" ? "Proyecto reactivado" : `Proyecto ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar el estatus");
    }
  };

  const deleteProject = async (p) => {
    try {
      await axios.delete(`${API}/viajeros-ext/projects/${p.id}`, { withCredentials: true });
      toast.success("Proyecto eliminado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar");
    }
  };


  const rotateSlug = async (p) => {
    if (!window.confirm("¿Rotar el link? El link anterior dejará de funcionar.")) return;
    await axios.post(`${API}/viajeros-ext/projects/${p.id}/rotate-slug`, {}, { withCredentials: true });
    toast.success("Link rotado");
    const r = await axios.get(`${API}/viajeros-ext/projects`, { withCredentials: true });
    setProjects(r.data || []);
  };

  const savePerfil = async () => {
    if (!Object.keys(edits).length) return toast.info("Sin cambios");
    setSaving(true);
    const pagoChanged = "pago_requiere" in edits;
    try {
      const r = await axios.patch(`${API}/viajeros-ext/perfiles/${detail.id}`, { updates: edits }, { withCredentials: true });
      setDetail(r.data);
      setEdits({});
      toast.success("Perfil actualizado");
      loadPerfiles();
      if (pagoChanged) setPagosRefreshKey((k) => k + 1);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const [deleteDialog, setDeleteDialog] = useState(null); // { id, folio, nombre }
  const [deleteMotivo, setDeleteMotivo] = useState("");
  const [deleting, setDeleting] = useState(false);
  const askDeletePerfil = (p) => {
    setDeleteDialog({ id: p.id, folio: p.folio, nombre: [(p.datos||{}).nombre, (p.datos||{}).apellido_paterno].filter(Boolean).join(" ") });
    setDeleteMotivo("");
  };
  const confirmDeletePerfil = async () => {
    if (!deleteDialog) return;
    if (!deleteMotivo.trim()) return toast.info("Escribe un motivo para dejar registro del porqué se eliminó");
    setDeleting(true);
    try {
      await axios.delete(`${API}/viajeros-ext/perfiles/${deleteDialog.id}`, {
        params: { motivo: deleteMotivo.trim() },
        withCredentials: true,
      });
      toast.success(`Perfil ${deleteDialog.folio} eliminado`);
      setDeleteDialog(null);
      setDeleteMotivo("");
      // Cerrar modal de detalle si estaba abierto para el mismo perfil
      if (detail?.id === deleteDialog.id) setDetail(null);
      loadPerfiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar");
    } finally {
      setDeleting(false);
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const r = await axios.get(`${API}/viajeros-ext/perfiles/${detail.id}/archivo/${doc.id}`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || "documento";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("No se pudo descargar"); }
  };

  const uploadDoc = async (campo, file, ev) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("campo", campo);
      fd.append("file", file);
      const r = await axios.post(`${API}/viajeros-ext/perfiles/${detail.id}/archivo`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Documento ${campo} actualizado`);
      // Merge nuevo doc en el detalle
      setDetail((prev) => {
        if (!prev) return prev;
        const docs = (prev.documentos || []).filter((d) => d.campo !== campo && d.tipo !== campo);
        return { ...prev, documentos: [...docs, r.data.document] };
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo subir el archivo");
    } finally {
      if (ev?.target) ev.target.value = "";
    }
  };

  const exportPerfil = async (perfilId, format) => {
    if (exportingPerfil) return; // Evita clicks duplicados durante la generación
    setExportingPerfil(format);
    try {
      const r = await axios.get(`${API}/viajeros-ext/perfiles/${perfilId}/export`, {
        params: { format },
        withCredentials: true,
        responseType: "blob",
      });
      const cd = r.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      const ext = format === "pdf" ? "pdf" : "xlsx";
      const name = m ? m[1] : `perfil.${ext}`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(`${format.toUpperCase()} descargado`);
    } catch (err) {
      // Como responseType es "blob", err.response.data es un Blob → parsearlo a JSON
      let msg = "No se pudo exportar el perfil";
      try {
        const blob = err.response?.data;
        if (blob && typeof blob.text === "function") {
          const txt = await blob.text();
          try { msg = JSON.parse(txt).detail || msg; } catch { if (txt) msg = txt; }
        } else if (err.response?.data?.detail) {
          msg = err.response.data.detail;
        }
      } catch { /* keep default msg */ }
      toast.error(msg);
    } finally {
      setExportingPerfil(null);
    }
  };

  const fetchClients = async (q) => {
    try {
      const r = await axios.get(`${API}/clients/search`, { params: { q: q || "" }, withCredentials: true });
      setClientOpts(r.data || []);
    } catch { /* noop */ }
  };

  const downloadBlob = (data, name) => {
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const projName = () => (projects.find((x) => x.id === filters.project_id)?.nombre || "proyecto").replace(/[^\w-]+/g, "_");

  const [exportOpen, setExportOpen] = useState(false);
  const [exportSel, setExportSel] = useState({}); // {project_id: bool}
  const [exportRange, setExportRange] = useState({ desde: "", hasta: "" });
  const openExportDialog = () => {
    // Precargar: si hay filtro de proyecto, marcarlo; si no, todo vacío
    const s = {};
    if (filters.project_id) s[filters.project_id] = true;
    setExportSel(s);
    setExportRange({ desde: "", hasta: "" });
    setExportOpen(true);
  };
  const exportExcel = async () => {
    const ids = Object.keys(exportSel).filter((k) => exportSel[k]);
    const params = {};
    if (ids.length > 0) params.project_ids = ids.join(",");
    if (exportRange.desde) params.desde = exportRange.desde;
    if (exportRange.hasta) params.hasta = exportRange.hasta;
    if (ids.length === 0 && !exportRange.desde && !exportRange.hasta) {
      const ok = window.confirm(
        "Vas a exportar TODOS los perfiles de TODOS los proyectos, sin filtro de fecha.\n\n" +
        "Puede tardar mucho tiempo con muchos registros.\n\n" +
        "Recomendado: exportar por períodos de máximo 1 año.\n\n¿Continuar?"
      );
      if (!ok) return;
    }
    try {
      const r = await axios.get(`${API}/viajeros-ext/export`, { params, withCredentials: true, responseType: "blob" });
      const label = ids.length === 1
        ? (projects.find((x) => x.id === ids[0])?.nombre || "proyecto").replace(/[^\w-]+/g, "_")
        : ids.length > 1 ? `${ids.length}_proyectos` : "todos";
      downloadBlob(r.data, `perfiles_${label}.xlsx`);
      setExportOpen(false);
    } catch { toast.error("No se pudo exportar"); }
  };

  const downloadManifiesto = async (includeFiles = false) => {
    if (manifDownloading) return; // evita cola de ejecuciones
    const fields = Object.keys(manifSel).filter((k) => manifSel[k] && k !== "__include_pagos_sheet__").join(",");
    const includePagosSheet = !!manifSel.__include_pagos_sheet__;
    const proj = projects.find((x) => x.id === filters.project_id);
    const allST = (proj?.subtipos || []).map((s) => s.id);
    const selST = Object.keys(manifSubtipoIds).filter((k) => manifSubtipoIds[k]);
    // Enviar solo si es un subconjunto (no si son todos, para dejar la query limpia)
    const subtipo_ids_param = (selST.length > 0 && selST.length < allST.length) ? selST.join(",") : "";
    setManifDownloading(includeFiles ? "zip" : "excel");
    setManifProgress(0);
    setManifConfirmFiles(false);
    setManifOpen(false);
    try {
      const params = { project_id: filters.project_id, fields, include_files: includeFiles ? 1 : 0, include_pagos_sheet: includePagosSheet ? 1 : 0 };
      if (subtipo_ids_param) params.subtipo_ids = subtipo_ids_param;
      const r = await axios.get(`${API}/viajeros-ext/manifiesto`, {
        params,
        withCredentials: true,
        responseType: "blob",
        onDownloadProgress: (evt) => {
          if (evt.total) {
            setManifProgress(Math.round((evt.loaded / evt.total) * 100));
          } else if (evt.loaded) {
            // total desconocido: pseudo-progreso
            setManifProgress((p) => Math.min(90, p + 3));
          }
        },
      });
      const ct = (r.headers?.["content-type"] || "").toLowerCase();
      const ext = ct.includes("zip") ? "zip" : "xlsx";
      downloadBlob(r.data, `manifiesto_${projName()}.${ext}`);
      setManifProgress(100);
      toast.success(includeFiles ? "Manifiesto y archivos descargados" : "Manifiesto descargado");
    } catch { toast.error("No se pudo generar el manifiesto"); }
    finally { setTimeout(() => { setManifDownloading(null); setManifProgress(0); }, 300); }
  };

  const dv = (k) => (k in edits ? edits[k] : (detail?.datos || {})[k]) ?? "";
  const sv = (k) => (k in edits ? edits[k] : detail?.[k]) ?? "";
  const setEdit = (k, v) => setEdits((e) => ({ ...e, [k]: v }));

  if (loading) return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Cargando…</div>;

  const projFiltrados = projects.filter((p) => {
    const st = p.project_status || "activo";
    if (projF.status !== "todos" && st !== projF.status) return false;
    if (projF.q.trim()) {
      const q = projF.q.trim().toLowerCase();
      if (![p.nombre, p.cliente_nombre, p.cliente_comercial, p.destinos].some((v) => (v || "").toLowerCase().includes(q))) return false;
    }
    return true;
  });
  const projStatusById = Object.fromEntries(projects.map((p) => [p.id, p.project_status || "activo"]));
  const perfilesVisibles = perfiles.filter((pf) => perfProjStatus === "todos" || (projStatusById[pf.project_id] || "activo") === perfProjStatus);

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="viajero-externo-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <ModuleHeaderIcon icon={Globe} />
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Perfil de Viajero · Externo</h1>
            <p className="text-sm text-slate-500 mt-1">Formularios públicos por proyecto para invitados de clientes.</p>
          </div>
        </div>
        <Button onClick={openNewProject} className="bg-cyan-700 hover:bg-cyan-800" data-testid="new-project-btn"><Plus className="w-4 h-4 mr-2" /> Nuevo proyecto</Button>
      </div>

      <Tabs defaultValue="proyectos">
        <TabsList>
          <TabsTrigger value="proyectos" data-testid="tab-proyectos">Proyectos ({projects.length})</TabsTrigger>
          <TabsTrigger value="perfiles" data-testid="tab-perfiles-ext">Perfiles ({perfiles.length})</TabsTrigger>
          {projects.some((p) => p.pago_servicios_config?.enabled) && (
            <TabsTrigger value="pagos" data-testid="tab-pagos">💳 Pagos</TabsTrigger>
          )}
          {canEdit && <TabsTrigger value="custom" data-testid="tab-custom-fields">Campos personalizados</TabsTrigger>}
        </TabsList>

        <TabsContent value="proyectos" className="space-y-4">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="w-64">
              <Label className="text-xs text-slate-500">Buscar proyecto o cliente</Label>
              <Input value={projF.q} placeholder="Nombre, cliente, destino…" onChange={(e) => setProjF({ ...projF, q: e.target.value })} data-testid="proj-filtro-q" />
            </div>
            <div className="w-44">
              <Label className="text-xs text-slate-500">Estatus del proyecto</Label>
              <Select value={projF.status} onValueChange={(v) => setProjF({ ...projF, status: v })}>
                <SelectTrigger data-testid="proj-filtro-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="archivado">Archivado</SelectItem>
                  <SelectItem value="cancelado">Cancelado</SelectItem>
                  <SelectItem value="todos">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(projF.q || projF.status !== "activo") && (
              <Button variant="ghost" size="sm" className="text-slate-400" onClick={() => setProjF({ q: "", status: "activo" })} data-testid="proj-filtro-limpiar">Limpiar</Button>
            )}
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Proyecto</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Estatus</TableHead>
                  <TableHead className="text-center">Perfiles</TableHead>
                  <TableHead className="text-center">Visitas</TableHead>
                  <TableHead>Idiomas</TableHead>
                  <TableHead>Link público</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projFiltrados.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">{projects.length === 0 ? "Sin proyectos. Crea el primero." : "Ningún proyecto coincide con los filtros."}</TableCell></TableRow>
                )}
                {projFiltrados.map((p) => (
                  <TableRow key={p.id} data-testid={`project-row-${p.id}`}>
                    <TableCell className="font-semibold text-slate-800">{p.nombre}</TableCell>
                    <TableCell className="text-sm text-slate-600">{p.cliente_comercial || p.cliente_nombre || "—"}</TableCell>
                    <TableCell>
                      {(p.project_status || "activo") === "activo"
                        ? <Badge className={`${p.activo ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"} border-0`}>{p.activo ? "Activo" : "Cerrado"}</Badge>
                        : <Badge className={`${p.project_status === "cancelado" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"} border-0`}>{p.project_status === "cancelado" ? "Cancelado" : "Archivado"}</Badge>}
                    </TableCell>
                    <TableCell className="text-center font-semibold text-slate-700">{p.perfiles_count}</TableCell>
                    <TableCell className="text-center text-slate-600">{p.visitas || 0}</TableCell>
                    <TableCell className="text-xs text-slate-500">ES{p.idiomas?.en ? " · EN" : ""}{p.idiomas?.fr ? " · FR" : ""}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-0.5">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Copiar link" onClick={() => copyLink(p)} data-testid={`copy-link-${p.id}`}><Copy className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Abrir" onClick={() => window.open(publicUrl(p), "_blank")}><ExternalLink className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Rotar link" onClick={() => rotateSlug(p)} data-testid={`rotate-slug-${p.id}`}><RefreshCw className="w-3.5 h-3.5" /></Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-0.5">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Configurar" onClick={() => { const pp = JSON.parse(JSON.stringify(p)); setActiveSubtipoIx(0); setProjDialog(loadSubtipoIntoDialog(pp, 0)); }} data-testid={`edit-project-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        {(p.subtipos || []).length > 0 && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-cyan-700" title={`Ver enlaces (${(p.subtipos||[]).length} subtipo${(p.subtipos||[]).length===1?"":"s"})`} onClick={() => setLinksPopup(p)} data-testid={`links-project-${p.id}`}><ExternalLink className="w-3.5 h-3.5" /></Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-cyan-800" title="Enviar link" onClick={() => setSendLink(p)} data-testid={`send-link-${p.id}`}><Mail className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-indigo-700" title="Estatus de invitaciones" onClick={() => setInvitationsFor(p)} data-testid={`invitations-${p.id}`}><Send className="w-3.5 h-3.5" /></Button>
                        {isAdmin && ((p.project_status || "activo") === "activo" ? (
                          <>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-amber-700" title="Archivar" onClick={() => setProjAction({ tipo: "archivado", p })} data-testid={`archivar-project-${p.id}`}><Archive className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" title="Cancelar" onClick={() => setProjAction({ tipo: "cancelado", p })} data-testid={`cancelar-project-${p.id}`}><Ban className="w-3.5 h-3.5" /></Button>
                          </>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-700" title="Reactivar" onClick={() => setProjAction({ tipo: "activo", p })} data-testid={`reactivar-project-${p.id}`}><ArchiveRestore className="w-3.5 h-3.5" /></Button>
                        ))}
                        {isAdmin && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" title={p.perfiles_count > 0 ? "Tiene registros: archívalo o cancélalo" : "Eliminar"} disabled={p.perfiles_count > 0} onClick={() => setProjAction({ tipo: "eliminar", p })} data-testid={`eliminar-project-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="perfiles" className="space-y-4">
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[["Total", stats.total], ["Últimos 7 días", stats.ultimos_7_dias], ["Llegadas próx. 7 días", stats.llegadas_7_dias], ["Visitas", stats.visitas], ["Conversión", `${stats.conversion}%`]].map(([l, v]) => (
                <Card key={l}><CardContent className="p-4"><p className="text-2xl font-extrabold text-cyan-800" data-testid={`kpi-${l}`}>{v}</p><p className="text-xs text-slate-500">{l}</p></CardContent></Card>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-end">
            <Select value={filters.project_id || "all"} onValueChange={(v) => { const f = { ...filters, project_id: v === "all" ? "" : v, subtipo_id: "" }; setFilters(f); loadPerfiles(f); }}>
              <SelectTrigger className="w-56" data-testid="filtro-proyecto"><SelectValue placeholder="Proyecto" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos los proyectos</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
            </Select>
            {filters.project_id && (() => {
              const proj = projects.find((x) => x.id === filters.project_id);
              const sts = (proj?.subtipos || []);
              if (sts.length < 1) return null;
              return (
                <Select value={filters.subtipo_id || "all"} onValueChange={(v) => { const f = { ...filters, subtipo_id: v === "all" ? "" : v }; setFilters(f); loadPerfiles(f); }}>
                  <SelectTrigger className="w-48" data-testid="filtro-subtipo"><SelectValue placeholder="Subtipo-cliente" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Subtipos: todos</SelectItem>
                    {sts.map((st) => <SelectItem key={st.id} value={st.id}>{st.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              );
            })()}
            <Select value={perfProjStatus} onValueChange={setPerfProjStatus}>
              <SelectTrigger className="w-48" data-testid="filtro-proyecto-status"><SelectValue placeholder="Estatus del proyecto" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Proyectos: todos</SelectItem>
                <SelectItem value="activo">Proyecto Activo</SelectItem>
                <SelectItem value="archivado">Proyecto Archivado</SelectItem>
                <SelectItem value="cancelado">Proyecto Cancelado</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.estatus || "all"} onValueChange={(v) => { const f = { ...filters, estatus: v === "all" ? "" : v }; setFilters(f); loadPerfiles(f); }}>
              <SelectTrigger className="w-40" data-testid="filtro-estatus"><SelectValue placeholder="Estatus" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{ESTATUS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
            <Input className="w-56" placeholder="Folio, nombre, email, empresa…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && loadPerfiles()} data-testid="filtro-busqueda" />
            <Input type="date" className="w-36" value={filters.desde} onChange={(e) => { const f = { ...filters, desde: e.target.value }; setFilters(f); loadPerfiles(f); }} data-testid="filtro-desde" />
            <Input type="date" className="w-36" value={filters.hasta} onChange={(e) => { const f = { ...filters, hasta: e.target.value }; setFilters(f); loadPerfiles(f); }} data-testid="filtro-hasta" />
            <Button variant="outline" onClick={() => loadPerfiles()} data-testid="btn-buscar">Buscar</Button>
            <Button variant="outline" onClick={openExportDialog} data-testid="btn-export"><Download className="w-4 h-4 mr-2" /> Excel</Button>
            <Button variant="outline" className="border-cyan-300 text-cyan-800 hover:bg-cyan-50" onClick={() => {
              if (!filters.project_id) return toast.error("Selecciona un proyecto");
              const proj = projects.find((x) => x.id === filters.project_id);
              // Paso 1: abrir popup de selección de subtipos (pre-seleccionar TODOS por defecto)
              const preST = {};
              (proj?.subtipos || []).forEach((st) => { preST[st.id] = true; });
              setManifSubtipoIds(preST);
              setManifSubtipoOpen(true);
            }} data-testid="btn-manifiesto"><Download className="w-4 h-4 mr-2" /> Manifiesto</Button>
          </div>
          <Card><CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="whitespace-nowrap">Fecha registro</TableHead>
                <TableHead className="whitespace-nowrap">Folio</TableHead>
                <TableHead className="whitespace-nowrap">Empresa BackOffice</TableHead>
                <TableHead className="whitespace-nowrap">Subtipo</TableHead>
                <TableHead className="whitespace-nowrap">Nombre</TableHead>
                <TableHead className="whitespace-nowrap">Correo</TableHead>
                <TableHead className="whitespace-nowrap">Proyecto</TableHead>
                <TableHead className="whitespace-nowrap">Empresa</TableHead>
                <TableHead className="whitespace-nowrap">Llegada</TableHead>
                <TableHead className="whitespace-nowrap">Otros proyectos</TableHead>
                <TableHead className="whitespace-nowrap">Estatus</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {perfiles.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-8">Sin perfiles.</TableCell></TableRow>}
                {perfilesVisibles.map((p) => {
                  const fecha = (p.registro_at || "").slice(0, 16).replace("T", " ");
                  const pr = projects.find((x) => x.id === p.project_id);
                  const empresaBO = pr?.cliente_comercial || pr?.cliente_nombre || "—";
                  const nombre = `${p.datos?.nombres || ""} ${p.datos?.apellidoP || ""}`.trim() || "—";
                  return (
                    <TableRow key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openPerfilDetail(p.id)} data-testid={`perfil-row-${p.folio}`}>
                      <TableCell className="text-xs text-slate-600 whitespace-nowrap font-mono">{fecha || "—"}</TableCell>
                      <TableCell className="font-mono font-semibold text-cyan-800 whitespace-nowrap">{p.folio}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap" data-testid={`empresa-backoffice-${p.folio}`}>{empresaBO}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {p.subtipo_nombre ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-cyan-100 text-cyan-800 border border-cyan-200">{p.subtipo_nombre}</span>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </TableCell>
                      <TableCell className="text-sm font-medium whitespace-nowrap">{nombre}</TableCell>
                      <TableCell className="text-xs text-slate-600 whitespace-nowrap">{p.datos?.email || "—"}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{pr?.nombre || "—"}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{p.empresa_asociada || "—"}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{p.datos?.llegadaFecha || "—"}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {p.otros_proyectos ? (
                          <button type="button" className="text-cyan-700 font-bold underline underline-offset-2 hover:text-cyan-900"
                            onClick={(e) => { e.stopPropagation(); const f = { project_id: "", estatus: "", q: p.datos?.email || "", desde: "", hasta: "" }; setFilters(f); loadPerfiles(f); toast.info("Mostrando todos los registros de este correo"); }}
                            data-testid={`otros-proyectos-${p.folio}`}>Sí</button>
                        ) : <span className="text-slate-400">No</span>}
                      </TableCell>
                      <TableCell className="whitespace-nowrap"><Badge className={`${EST_CLS[p.estatus] || ""} border-0`}>{p.estatus}</Badge></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="pagos" className="space-y-4">
          <Card><CardContent className="pt-6">
            <PagosTab projects={projects} canEdit={canEdit} users={usersList} refreshKey={pagosRefreshKey} onExternalRefresh={() => loadPerfiles()} />
          </CardContent></Card>
        </TabsContent>

        {canEdit && (
          <TabsContent value="custom" className="space-y-4">
            <Card><CardContent className="pt-6">
              <CustomFieldsPanel baseCatalog={catalogo} onChanged={() => loadCatalogo()} />
            </CardContent></Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Enviar link por correo */}
      {sendLink && <SendLinkDialog project={sendLink} onClose={() => setSendLink(null)} />}
      {invitationsFor && <InvitationsDialog project={invitationsFor} onClose={() => setInvitationsFor(null)} />}

      {/* Manifiesto: Paso 1 — selección de subtipos-cliente */}
      <Dialog open={manifSubtipoOpen} onOpenChange={setManifSubtipoOpen}>
        <DialogContent className="max-w-md" data-testid="manifiesto-subtipo-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Download className="w-5 h-5 text-cyan-700" /> Manifiesto — subtipos a incluir</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Elige qué subtipos-cliente incluir en el manifiesto. Al continuar, podrás seleccionar los campos.</p>
          {(() => {
            const proj = projects.find((x) => x.id === filters.project_id);
            const sts = proj?.subtipos || [];
            const total = sts.length;
            const selected = Object.keys(manifSubtipoIds).filter((k) => manifSubtipoIds[k]).length;
            const allSelected = total > 0 && selected === total;
            return (
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-lg bg-cyan-50 border border-cyan-200 px-3 py-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-cyan-800 cursor-pointer">
                    <Checkbox checked={allSelected} onCheckedChange={(v) => {
                      const next = {};
                      sts.forEach((st) => { next[st.id] = !!v; });
                      setManifSubtipoIds(next);
                    }} data-testid="manif-subtipo-todos" />
                    Todos los subtipos ({total})
                  </label>
                  <span className="text-xs text-cyan-700">{selected} / {total} seleccionados</span>
                </div>
                <div className="max-h-64 overflow-y-auto space-y-1 border border-slate-200 rounded-lg p-2 bg-white">
                  {sts.map((st) => (
                    <label key={st.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer text-sm">
                      <Checkbox checked={!!manifSubtipoIds[st.id]} onCheckedChange={(v) => setManifSubtipoIds((s) => ({ ...s, [st.id]: !!v }))} data-testid={`manif-subtipo-${st.id}`} />
                      <span className="font-medium text-slate-700">{st.nombre}</span>
                    </label>
                  ))}
                  {sts.length === 0 && <p className="text-xs text-slate-400 px-2 py-1.5">Este proyecto no tiene subtipos.</p>}
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setManifSubtipoOpen(false)} data-testid="manif-subtipo-cancelar">Cancelar</Button>
            <Button
              className="bg-cyan-700 hover:bg-cyan-800"
              disabled={Object.keys(manifSubtipoIds).filter((k) => manifSubtipoIds[k]).length === 0}
              onClick={() => {
                // Continuar: abrir popup de campos (paso 2)
                const proj = projects.find((x) => x.id === filters.project_id);
                const fc = proj?.fields_config || {};
                const isFieldActive = (sk, fk) => {
                  const sec = fc[sk];
                  if (!sec) return true;
                  if (sec.on === false && sk !== "consentimientos") return false;
                  return (sec.f || {})[fk] !== false;
                };
                const preSel = { folio: true };
                (catalogo?.sections || []).forEach((s) => {
                  s.fields.forEach((f) => { if (isFieldActive(s.key, f.k)) preSel[f.k] = true; });
                });
                if (proj?.pago_servicios_config?.enabled) {
                  preSel.pago_requiere = true;
                  preSel.pago_servicios = true;
                  preSel.pago_total = true;
                  preSel.pago_estatus = true;
                }
                setManifSel(preSel);
                setManifSubtipoOpen(false);
                setManifOpen(true);
              }}
              data-testid="manif-subtipo-continuar"
            >Continuar <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manifiesto: selección de campos */}
      <Dialog open={manifOpen} onOpenChange={setManifOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="manifiesto-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Download className="w-5 h-5 text-cyan-700" /> Manifiesto — elegir campos</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">El manifiesto incluye los datos del proyecto en el encabezado. Elige qué columnas quieres por viajero.</p>
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
              <Checkbox checked disabled />
              <span className="text-sm font-semibold text-slate-700">Nombre del viajero</span>
              <span className="text-[11px] text-slate-400">(siempre incluido)</span>
              <label className="flex items-center gap-2 ml-auto text-sm text-slate-600 cursor-pointer">
                <Checkbox checked={!!manifSel.folio} onCheckedChange={(v) => setManifSel((s) => ({ ...s, folio: !!v }))} data-testid="manif-folio" /> Folio
              </label>
            </div>
            {catalogo?.sections.map((s) => (
              <div key={s.key} className="border border-slate-200 rounded-lg px-3 py-2">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{s.es}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                  {s.fields.map((f) => (
                    <label key={f.k} className="flex items-center gap-2 text-sm text-slate-600 py-0.5 cursor-pointer">
                      <Checkbox checked={!!manifSel[f.k]} onCheckedChange={(v) => setManifSel((sel) => ({ ...sel, [f.k]: !!v }))} data-testid={`manif-${f.k}`} />
                      <span className="truncate">{f.col}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            {/* Sección Pago de Servicios (solo si el proyecto filtrado la tiene activa) */}
            {(() => {
              const proj = projects.find((x) => x.id === filters.project_id);
              const pagoOn = !!(proj?.pago_servicios_config?.enabled);
              if (!pagoOn) return null;
              const pagoFields = [
                { k: "pago_requiere", col: "¿Requiere servicio?" },
                { k: "pago_servicios", col: "Servicios seleccionados" },
                { k: "pago_total", col: "Total del servicio (MXN)" },
                { k: "pago_medios", col: "Medios de contacto" },
                { k: "pago_estatus", col: "Estatus de seguimiento" },
              ];
              return (
                <div className="border-2 border-amber-300 bg-amber-50/40 rounded-lg px-3 py-2">
                  <p className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <CreditCard className="w-3.5 h-3.5" /> Pago de Servicios
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                    {pagoFields.map((f) => (
                      <label key={f.k} className="flex items-center gap-2 text-sm text-slate-600 py-0.5 cursor-pointer">
                        <Checkbox checked={!!manifSel[f.k]} onCheckedChange={(v) => setManifSel((sel) => ({ ...sel, [f.k]: !!v }))} data-testid={`manif-${f.k}`} />
                        <span className="truncate">{f.col}</span>
                      </label>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 text-sm text-amber-900 font-semibold mt-2 pt-2 border-t border-amber-200 cursor-pointer">
                    <Checkbox checked={!!manifSel.__include_pagos_sheet__} onCheckedChange={(v) => setManifSel((sel) => ({ ...sel, __include_pagos_sheet__: !!v }))} data-testid="manif-include-pagos-sheet" />
                    <span>Incluir pestaña "Pagos" (hoja adicional con el reporte completo del tablero)</span>
                  </label>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManifOpen(false)}>Cancelar</Button>
            <Button onClick={() => setManifConfirmFiles(true)} className="bg-cyan-700 hover:bg-cyan-800" data-testid="manif-descargar"><Download className="w-4 h-4 mr-2" /> Descargar manifiesto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación: incluir archivos adjuntos en un ZIP */}
      <Dialog open={manifConfirmFiles} onOpenChange={(o) => { if (!o) setManifConfirmFiles(false); }}>
        <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" data-testid="manif-confirm-files-dialog">
          {/* Header con acento cyan */}
          <div className="px-6 pt-5 pb-4 border-b bg-gradient-to-br from-cyan-50 to-white">
            <DialogHeader className="space-y-1">
              <DialogTitle className="flex items-center gap-2.5 text-base font-semibold text-slate-900 normal-case tracking-normal">
                <div className="w-8 h-8 rounded-lg bg-cyan-100 border border-cyan-200 flex items-center justify-center flex-shrink-0">
                  <Download className="w-4 h-4 text-cyan-700" />
                </div>
                Incluir archivos adjuntos
              </DialogTitle>
              <p className="text-xs text-slate-500 pl-[42px]">Elige el formato de descarga del manifiesto</p>
            </DialogHeader>
          </div>

          {/* Contenido */}
          <div className="px-6 py-5 space-y-3.5 text-sm text-slate-700 leading-relaxed">
            <p>
              Puedes descargar solo el <b>Excel</b> o generar un <b>ZIP</b> que incluya el Excel más todos los archivos adjuntos
              (pasaporte, INE, etc.) de los viajeros del proyecto.
            </p>
            <div className="rounded-lg border border-cyan-200 bg-cyan-50/60 p-3.5 text-xs text-cyan-900 leading-relaxed">
              <div className="font-semibold text-cyan-800 mb-1 flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-500" />
                Al incluir archivos
              </div>
              El Excel mostrará el nombre del archivo como <b>hipervínculo</b>. Un solo clic abre el documento desde la carpeta <span className="font-mono text-[11px] bg-white/70 border border-cyan-200 rounded px-1 py-0.5">documentos/</span> del ZIP.
            </div>
            <p className="text-xs text-slate-400 flex items-start gap-1.5">
              <span className="inline-block w-1 h-1 rounded-full bg-slate-300 mt-1.5 flex-shrink-0" />
              Si el proyecto tiene muchos viajeros con archivos, la descarga puede tardar unos segundos.
            </p>
          </div>

          {/* Footer con acciones */}
          <DialogFooter className="px-6 py-4 border-t bg-slate-50 flex-row items-center justify-end gap-2 sm:gap-2">
            <Button variant="ghost" size="sm" onClick={() => setManifConfirmFiles(false)} disabled={!!manifDownloading} className="text-slate-600">Cancelar</Button>
            <Button variant="outline" size="sm" onClick={() => downloadManifiesto(false)} disabled={!!manifDownloading} data-testid="manif-only-excel">
              {manifDownloading === "excel" ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Generando…</> : "Solo Excel"}
            </Button>
            <Button size="sm" className="bg-cyan-700 hover:bg-cyan-800 text-white shadow-sm" onClick={() => downloadManifiesto(true)} disabled={!!manifDownloading} data-testid="manif-with-files">
              {manifDownloading === "zip" ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Generando…</> : <><Download className="w-3.5 h-3.5 mr-1.5" /> Excel + archivos</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Overlay bloqueante mientras se genera el manifiesto (evita clicks repetidos) */}
      {manifDownloading && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          aria-live="polite"
          data-testid="manif-processing"
        >
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-sm w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 pt-6 pb-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-cyan-100 border border-cyan-200 flex items-center justify-center flex-shrink-0">
                <Loader2 className="w-6 h-6 text-cyan-700 animate-spin" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-slate-900">Generando manifiesto</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {manifDownloading === "zip"
                    ? "Empaquetando Excel y archivos adjuntos en un ZIP…"
                    : "Preparando tu Excel…"}
                </p>
              </div>
            </div>
            {/* Progress bar */}
            <div className="px-6 pb-3">
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-full transition-all duration-300 ease-out"
                  style={{ width: manifProgress ? `${manifProgress}%` : "20%" }}
                />
              </div>
              <div className="flex items-center justify-between mt-1.5 text-[11px] text-slate-500">
                <span>{manifProgress ? `${manifProgress}%` : "Preparando…"}</span>
                <span>Por favor no cierres esta ventana</span>
              </div>
            </div>
            <div className="px-6 py-3 bg-slate-50 border-t text-[11px] text-slate-400 leading-relaxed">
              La descarga arrancará automáticamente al terminar. Este proceso puede tardar varios segundos según el número de viajeros y archivos.
            </div>
          </div>
        </div>
      )}

      {/* Exportar Excel: selección de proyectos + rango de fechas */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Download className="w-5 h-5 text-cyan-700" /> Exportar perfiles a Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm">
              Si no eliges ningún proyecto, se exportarán TODOS los perfiles registrados. Puede tardar mucho.
              <br /><b>Recomendación:</b> exportar por períodos de máximo 1 año.
            </div>
            <div>
              <Label className="text-sm">Proyectos a incluir (deja vacío para exportar todo)</Label>
              <div className="mt-2 max-h-52 overflow-y-auto border rounded-md p-2 space-y-1">
                {projects.length === 0 ? (
                  <p className="text-sm text-slate-400 p-2">Sin proyectos</p>
                ) : projects.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm px-2 py-1 hover:bg-slate-50 rounded cursor-pointer">
                    <input type="checkbox" checked={!!exportSel[p.id]}
                      onChange={(e) => setExportSel((s) => ({ ...s, [p.id]: e.target.checked }))}
                      data-testid={`export-chk-${p.id}`} />
                    <span className="font-medium">{p.nombre}</span>
                    <span className="text-slate-400 text-xs ml-auto">{p.cliente_nombre || ""}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-2 mt-2 text-xs">
                <button className="text-cyan-700 hover:underline" onClick={() => setExportSel(Object.fromEntries(projects.map((p) => [p.id, true])))}>Seleccionar todos</button>
                <button className="text-slate-500 hover:underline" onClick={() => setExportSel({})}>Limpiar</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Desde</Label>
                <Input type="date" value={exportRange.desde} onChange={(e) => setExportRange((r) => ({ ...r, desde: e.target.value }))} data-testid="export-desde" />
              </div>
              <div>
                <Label className="text-sm">Hasta</Label>
                <Input type="date" value={exportRange.hasta} onChange={(e) => setExportRange((r) => ({ ...r, hasta: e.target.value }))} data-testid="export-hasta" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportOpen(false)}>Cancelar</Button>
            <Button onClick={exportExcel} className="bg-cyan-700 hover:bg-cyan-800" data-testid="export-descargar"><Download className="w-4 h-4 mr-2" /> Descargar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editor de proyecto */}
      <Dialog open={!!projDialog} onOpenChange={(o) => !o && setProjDialog(null)}>
        <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="project-dialog">
          {projDialog && catalogo && (<>
            <DialogHeader><DialogTitle>{projDialog.id ? "Configurar proyecto" : "Nuevo proyecto"}</DialogTitle></DialogHeader>
            {/* Barra de subtipos (solo cuando el proyecto ya está guardado) */}
            {projDialog.id && (projDialog.subtipos || []).length > 0 && (
              <div className="border border-slate-200 bg-slate-50/60 rounded-lg p-2 mb-2" data-testid="subtipos-strip">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Subtipo-Cliente</span>
                  <span className="text-[10px] text-slate-400">Estos configuran preguntas, pago y bienvenida por separado. Cliente y diseño se comparten.</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(projDialog.subtipos || []).map((st, i) => (
                    <button key={st.id} type="button" onClick={() => switchSubtipo(i)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${i === activeSubtipoIx ? "bg-cyan-600 border-cyan-600 text-white font-semibold" : "bg-white border-slate-300 text-slate-700 hover:border-cyan-400"}`}
                      data-testid={`subtipo-pill-${i}`}
                    >{st.nombre}{i === 0 ? " (base)" : ""}</button>
                  ))}
                  <button type="button" onClick={() => setSubtipoNewOpen(true)} disabled={(projDialog.subtipos || []).length >= 54}
                    className="text-xs px-2.5 py-1 rounded-full border border-dashed border-emerald-500 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                    data-testid="add-subtipo-btn"
                  >+ Agregar subtipo</button>
                  {activeSubtipoIx > 0 && activeSubtipo && (
                    <button type="button" onClick={() => removeSubtipo(activeSubtipo.id)}
                      className="text-xs px-2.5 py-1 rounded-full border border-rose-300 text-rose-700 hover:bg-rose-50"
                      data-testid="delete-subtipo-btn"
                    ><Trash2 className="w-3 h-3 inline-block mr-1" />Eliminar "{activeSubtipo.nombre}"</button>
                  )}
                </div>
              </div>
            )}
            <Tabs defaultValue="general">
              <TabsList>
                <TabsTrigger value="general" data-testid="proj-tab-general">General</TabsTrigger>
                <TabsTrigger value="preguntas" data-testid="proj-tab-preguntas">Preguntas</TabsTrigger>
                <TabsTrigger value="diseno" data-testid="proj-tab-diseno">Diseño</TabsTrigger>
              </TabsList>
              <TabsContent value="general" className="pt-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Nombre del proyecto *</Label><Input value={projDialog.nombre || ""} onChange={(e) => setProjDialog({ ...projDialog, nombre: e.target.value })} data-testid="proj-nombre" /></div>
                  <div className="space-y-1.5">
                    <Label>Cliente *</Label>
                    <ClientCombobox value={projDialog.cliente_nombre || ""} testid="proj-cliente"
                      onSelect={(c) => setProjDialog({ ...projDialog, cliente_nombre: c.company_name, cliente_id: c.id, cliente_comercial: c.nombre_comercial || "" })}
                      onClear={() => setProjDialog({ ...projDialog, cliente_nombre: "", cliente_id: "" })} />
                    <p className="text-[11px] text-slate-400">Se obtiene de la base de clientes.</p>
                    <label className="flex items-center gap-2 text-xs cursor-pointer pt-1">
                      <input type="checkbox"
                        checked={!!projDialog.titulo_publico_activo}
                        onChange={(e) => setProjDialog({ ...projDialog, titulo_publico_activo: e.target.checked })}
                        data-testid="proj-titulo-publico-activo" />
                      <span className="text-slate-700">Nombre Opcional (personalizar el título del formulario)</span>
                    </label>
                    {projDialog.titulo_publico_activo && (
                      <div className="pt-1">
                        <Input value={projDialog.titulo_publico || ""}
                          placeholder="Ej. JLR Summit 2026 · Registro de asistentes"
                          maxLength={120}
                          onChange={(e) => setProjDialog({ ...projDialog, titulo_publico: e.target.value })}
                          data-testid="proj-titulo-publico" />
                        <p className="text-[11px] text-slate-400 mt-1">Este texto reemplazará "{(projDialog.cliente_comercial || projDialog.cliente_nombre || "Cliente")} | {projDialog.nombre || "Proyecto"}" en el encabezado del formulario público.</p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Nombre comercial del cliente</Label>
                    <Input value={projDialog.cliente_comercial || ""} placeholder="Se muestra en la vista del proyecto"
                      onChange={(e) => setProjDialog({ ...projDialog, cliente_comercial: e.target.value })} data-testid="proj-cliente-comercial" />
                    <p className="text-[11px] text-slate-400">Se llena automático desde la base de clientes; puedes ajustarlo.</p>
                  </div>
                  <div className="space-y-1.5"><Label>Nombre del Contacto *</Label><Input value={projDialog.contacto_nombre || ""} onChange={(e) => setProjDialog({ ...projDialog, contacto_nombre: e.target.value })} data-testid="proj-contacto-nombre" /></div>
                  <div className="space-y-1.5"><Label>Teléfono del contacto *</Label><Input value={projDialog.contacto_telefono || ""} placeholder="+52 5512345678" onChange={(e) => setProjDialog({ ...projDialog, contacto_telefono: e.target.value })} data-testid="proj-contacto-telefono" /></div>
                  <div className="space-y-1.5">
                    <Label>Encargado del Proyecto *</Label>
                    <select className="w-full h-9 border border-input rounded-md px-3 text-sm bg-transparent" value={projDialog.encargado || ""}
                      onChange={(e) => setProjDialog({ ...projDialog, encargado: e.target.value })} data-testid="proj-encargado">
                      <option value="">—</option>
                      {usersList.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5"><Label>Destino(s) *</Label><Input value={projDialog.destinos || ""} placeholder="CDMX, Cancún…" onChange={(e) => setProjDialog({ ...projDialog, destinos: e.target.value })} data-testid="proj-destinos" /></div>
                  <div className="space-y-1.5"><Label>Fecha Inicio *</Label><Input type="date" value={projDialog.fecha_inicio || ""} onChange={(e) => setProjDialog({ ...projDialog, fecha_inicio: e.target.value })} data-testid="proj-fecha-inicio" /></div>
                  <div className="space-y-1.5"><Label>Fecha Fin *</Label><Input type="date" value={projDialog.fecha_fin || ""} onChange={(e) => setProjDialog({ ...projDialog, fecha_fin: e.target.value })} data-testid="proj-fecha-fin" /></div>
                  <div className="flex items-center gap-6 md:col-span-2">
                    <label className="flex items-center gap-2 text-sm"><Switch checked={!!projDialog.activo} onCheckedChange={(v) => setProjDialog({ ...projDialog, activo: v })} data-testid="proj-activo" /> Formulario activo</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!projDialog.idiomas?.en} onCheckedChange={(v) => setProjDialog({ ...projDialog, idiomas: { ...projDialog.idiomas, en: !!v } })} data-testid="proj-en" /> Inglés</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!projDialog.idiomas?.fr} onCheckedChange={(v) => setProjDialog({ ...projDialog, idiomas: { ...projDialog.idiomas, fr: !!v } })} data-testid="proj-fr" /> Francés</label>
                  </div>
                  <div className="space-y-1.5"><Label>Notificar a (emails separados por coma)</Label><Input value={Array.isArray(projDialog.notificar_a) ? projDialog.notificar_a.join(", ") : (projDialog.notificar_a || "")} placeholder="correo1@pdtravelmex.com, correo2@pdtravelmex.com" onChange={(e) => setProjDialog({ ...projDialog, notificar_a: e.target.value })} data-testid="proj-notificar" /></div>
                  <div className="space-y-1.5"><Label>Aviso de privacidad (URL)</Label><Input value={projDialog.aviso_privacidad_url || ""} placeholder="https://pdtravelmex.com/aviso-de-privacidad/" onChange={(e) => setProjDialog({ ...projDialog, aviso_privacidad_url: e.target.value })} /></div>
                  <div className="space-y-1.5 md:col-span-2"><Label>Mensaje de bienvenida (ES, opcional)</Label><Textarea rows={2} value={projDialog.mensaje_bienvenida?.es || ""} onChange={(e) => setProjDialog({ ...projDialog, mensaje_bienvenida: { ...projDialog.mensaje_bienvenida, es: e.target.value } })} /></div>
                  <div className="md:col-span-2 border border-amber-200 rounded-lg p-4 bg-amber-50/40 space-y-3">
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <Checkbox
                        checked={!!projDialog.pago_servicios_config?.enabled}
                        onCheckedChange={(v) => setProjDialog({ ...projDialog, pago_servicios_config: { ...(projDialog.pago_servicios_config || {}), enabled: !!v } })}
                        data-testid="proj-pago-servicios-enabled"
                      />
                      <div className="text-sm">
                        <div className="font-semibold text-slate-800">💳 Incluir sección de Pago de Servicios</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          Tú defines el servicio, costo y tiempo de contacto. El viajero solo elige si lo requiere y por qué medio contactarlo.
                        </div>
                      </div>
                    </label>
                    {projDialog.pago_servicios_config?.enabled && (() => {
                      const psc = projDialog.pago_servicios_config || {};
                      const servicios = psc.servicios || [];
                      const upd = (patch) => setProjDialog({ ...projDialog, pago_servicios_config: { ...psc, ...patch } });
                      const updSrv = (idx, patch) => {
                        const next = servicios.map((s, i) => i === idx ? { ...s, ...patch } : s);
                        upd({ servicios: next });
                      };
                      const addSrv = () => {
                        if (servicios.length >= 3) return;
                        upd({ servicios: [...servicios, { id: `srv_new_${Date.now()}`, nombre: "", descripcion: "", costo: 0 }] });
                      };
                      const delSrv = (idx) => upd({ servicios: servicios.filter((_, i) => i !== idx) });
                      const totalSec = (catalogo?.sections || []).length + 1;
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-amber-200 pt-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs">Título de la sección (ES) *</Label>
                            <Input value={psc.titulo || ""} placeholder="Ej. Pago de Servicios" onChange={(e) => upd({ titulo: e.target.value })} data-testid="proj-pago-titulo" />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs">Posición entre las secciones</Label>
                            <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white" value={psc.posicion || 999} onChange={(e) => upd({ posicion: parseInt(e.target.value, 10) })} data-testid="proj-pago-posicion">
                              {Array.from({ length: totalSec }, (_, i) => (
                                <option key={i + 1} value={i + 1}>Posición {i + 1}</option>
                              ))}
                              <option value={999}>Al final</option>
                            </select>
                          </div>
                          {projDialog.idiomas?.en && (
                            <div className="space-y-1.5">
                              <Label className="text-xs">Título (EN)</Label>
                              <Input value={psc.titulo_en || ""} placeholder="Service Payment" onChange={(e) => upd({ titulo_en: e.target.value })} data-testid="proj-pago-titulo-en" />
                            </div>
                          )}
                          {projDialog.idiomas?.fr && (
                            <div className="space-y-1.5">
                              <Label className="text-xs">Título (FR)</Label>
                              <Input value={psc.titulo_fr || ""} placeholder="Paiement de service" onChange={(e) => upd({ titulo_fr: e.target.value })} data-testid="proj-pago-titulo-fr" />
                            </div>
                          )}
                          <div className="space-y-1.5">
                            <Label className="text-xs">Tiempo de contacto (para todos los servicios) *</Label>
                            <div className="flex gap-2">
                              <Input type="number" min="1" value={psc.tiempo_contacto_valor || 0} onChange={(e) => upd({ tiempo_contacto_valor: parseInt(e.target.value, 10) || 0 })} data-testid="proj-pago-tiempo-valor" />
                              <select className="w-32 h-9 border border-slate-200 rounded-md px-2 text-sm bg-white" value={psc.tiempo_contacto_unidad || "horas"} onChange={(e) => upd({ tiempo_contacto_unidad: e.target.value })} data-testid="proj-pago-tiempo-unidad">
                                <option value="horas">horas</option>
                                <option value="minutos">minutos</option>
                              </select>
                            </div>
                          </div>

                          {/* Servicios (hasta 3) */}
                          <div className="md:col-span-2 space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs font-bold text-slate-700">Servicios ofrecidos ({servicios.length}/3)</Label>
                              <div className="flex gap-2">
                                {(projDialog.idiomas?.en || projDialog.idiomas?.fr) && (servicios.some((s) => s.nombre || s.descripcion) || psc.boton_si || psc.boton_no || psc.texto_no) && (
                                  <Button
                                    type="button" size="sm" variant="outline"
                                    onClick={async () => {
                                      const langs = [];
                                      if (projDialog.idiomas?.en) langs.push("en");
                                      if (projDialog.idiomas?.fr) langs.push("fr");
                                      if (!langs.length) return;
                                      try {
                                        toast.info(`Traduciendo a ${langs.join(", ").toUpperCase()}…`);
                                        // 1) Texto plano: título + botones Sí/No + nombres
                                        const nombreItems = [
                                          ...(psc.titulo ? [{ id: "__titulo__", text: psc.titulo }] : []),
                                          ...(psc.boton_si ? [{ id: "__boton_si__", text: psc.boton_si }] : []),
                                          ...(psc.boton_no ? [{ id: "__boton_no__", text: psc.boton_no }] : []),
                                          ...servicios.map((s, i) => ({ id: `nombre_${i}`, text: s.nombre || "" })).filter((x) => x.text),
                                        ];
                                        // 2) HTML: mensaje "No" + descripciones
                                        const descItems = [
                                          ...(psc.texto_no ? [{ id: "__texto_no__", text: psc.texto_no }] : []),
                                          ...servicios.map((s, i) => ({ id: `desc_${i}`, text: s.descripcion || "" })).filter((x) => x.text),
                                        ];
                                        const [nombresRes, descRes] = await Promise.all([
                                          nombreItems.length ? axios.post(`${API}/viajeros-ext/translate`, { items: nombreItems, target_langs: langs, is_html: false }) : Promise.resolve({ data: { translations: {} } }),
                                          descItems.length ? axios.post(`${API}/viajeros-ext/translate`, { items: descItems, target_langs: langs, is_html: true }) : Promise.resolve({ data: { translations: {} } }),
                                        ]);
                                        // Aplicar traducciones a servicios
                                        const nextServicios = servicios.map((s, i) => {
                                          const nT = nombresRes.data.translations[`nombre_${i}`] || {};
                                          const dT = descRes.data.translations[`desc_${i}`] || {};
                                          return {
                                            ...s,
                                            ...(langs.includes("en") ? { nombre_en: nT.en || s.nombre_en, descripcion_en: dT.en || s.descripcion_en } : {}),
                                            ...(langs.includes("fr") ? { nombre_fr: nT.fr || s.nombre_fr, descripcion_fr: dT.fr || s.descripcion_fr } : {}),
                                          };
                                        });
                                        const titTr = nombresRes.data.translations["__titulo__"] || {};
                                        const siTr = nombresRes.data.translations["__boton_si__"] || {};
                                        const noTr = nombresRes.data.translations["__boton_no__"] || {};
                                        const txtNoTr = descRes.data.translations["__texto_no__"] || {};
                                        upd({
                                          servicios: nextServicios,
                                          ...(langs.includes("en") ? {
                                            titulo_en: titTr.en || psc.titulo_en,
                                            boton_si_en: siTr.en || psc.boton_si_en,
                                            boton_no_en: noTr.en || psc.boton_no_en,
                                            texto_no_en: txtNoTr.en || psc.texto_no_en,
                                          } : {}),
                                          ...(langs.includes("fr") ? {
                                            titulo_fr: titTr.fr || psc.titulo_fr,
                                            boton_si_fr: siTr.fr || psc.boton_si_fr,
                                            boton_no_fr: noTr.fr || psc.boton_no_fr,
                                            texto_no_fr: txtNoTr.fr || psc.texto_no_fr,
                                          } : {}),
                                        });
                                        toast.success("Traducción completada. Revisa y guarda el proyecto.");
                                      } catch (e) {
                                        toast.error(e.response?.data?.detail || "No se pudo traducir");
                                      }
                                    }}
                                    className="h-7 text-xs border-blue-400 text-blue-700 hover:bg-blue-50"
                                    data-testid="proj-pago-traducir"
                                  >
                                    🌐 Traducir a {[projDialog.idiomas?.en && "EN", projDialog.idiomas?.fr && "FR"].filter(Boolean).join(" · ")}
                                  </Button>
                                )}
                                {servicios.length < 3 && (
                                  <Button type="button" size="sm" variant="outline" onClick={addSrv} className="h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="proj-pago-add-servicio">
                                    + Agregar servicio
                                  </Button>
                                )}
                              </div>
                            </div>
                            {servicios.length === 0 && (
                              <p className="text-xs text-slate-400 italic border border-dashed border-slate-300 rounded p-3 text-center">Agrega al menos 1 servicio (máx. 3).</p>
                            )}
                            {servicios.map((s, i) => (
                              <div key={s.id || i} className="rounded-md border border-amber-300 bg-white p-3 space-y-2" data-testid={`proj-pago-srv-${i}`}>
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] font-bold uppercase text-amber-700">Servicio {i + 1}</span>
                                  <button type="button" onClick={() => delSrv(i)} className="text-red-500 hover:text-red-700 text-xs" data-testid={`proj-pago-srv-${i}-remove`}>✕ Quitar</button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                  <div className="md:col-span-2 space-y-1">
                                    <Label className="text-[11px]">Nombre del servicio *</Label>
                                    <Input value={s.nombre || ""} placeholder="Ej. Traslado privado aeropuerto - hotel" onChange={(e) => updSrv(i, { nombre: e.target.value })} data-testid={`proj-pago-srv-${i}-nombre`} />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-[11px]">Costo (MXN) *</Label>
                                    <Input type="number" min="0" step="0.01" value={s.costo || 0} onChange={(e) => updSrv(i, { costo: parseFloat(e.target.value) || 0 })} data-testid={`proj-pago-srv-${i}-costo`} />
                                  </div>
                                </div>
                                {projDialog.idiomas?.en && (
                                  <div className="space-y-1">
                                    <Label className="text-[11px] text-blue-700">Nombre (EN)</Label>
                                    <Input value={s.nombre_en || ""} placeholder="Auto-completed with Translate button" onChange={(e) => updSrv(i, { nombre_en: e.target.value })} data-testid={`proj-pago-srv-${i}-nombre-en`} />
                                  </div>
                                )}
                                {projDialog.idiomas?.fr && (
                                  <div className="space-y-1">
                                    <Label className="text-[11px] text-blue-700">Nom (FR)</Label>
                                    <Input value={s.nombre_fr || ""} placeholder="Rempli avec le bouton Traduire" onChange={(e) => updSrv(i, { nombre_fr: e.target.value })} data-testid={`proj-pago-srv-${i}-nombre-fr`} />
                                  </div>
                                )}
                                <div className="space-y-1">
                                  <Label className="text-[11px]">Descripción (opcional)</Label>
                                  <RichTextEditor
                                    value={s.descripcion || ""}
                                    onChange={(html) => updSrv(i, { descripcion: html })}
                                    placeholder="Detalle adicional que verá el viajero: incluye, no incluye, duración, etc. Puedes usar negritas, listas, colores…"
                                    minHeight={140}
                                  />
                                </div>
                                {projDialog.idiomas?.en && (
                                  <div className="space-y-1">
                                    <Label className="text-[11px] text-blue-700">Description (EN)</Label>
                                    <RichTextEditor
                                      value={s.descripcion_en || ""}
                                      onChange={(html) => updSrv(i, { descripcion_en: html })}
                                      placeholder="Auto-completed with Translate button"
                                      minHeight={110}
                                    />
                                  </div>
                                )}
                                {projDialog.idiomas?.fr && (
                                  <div className="space-y-1">
                                    <Label className="text-[11px] text-blue-700">Description (FR)</Label>
                                    <RichTextEditor
                                      value={s.descripcion_fr || ""}
                                      onChange={(html) => updSrv(i, { descripcion_fr: html })}
                                      placeholder="Rempli avec le bouton Traduire"
                                      minHeight={110}
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Botones y mensaje personalizables (Sí/No) */}
                          <div className="md:col-span-2 rounded-md border border-amber-300 bg-white p-3 space-y-3" data-testid="proj-pago-textos-botones">
                            <div>
                              <Label className="text-xs font-bold text-slate-700">Textos personalizables</Label>
                              <p className="text-[11px] text-slate-500 mt-0.5">Personaliza lo que verá el viajero en los botones de aceptar/rechazar y qué mensaje aparece cuando responde <b>No</b>.</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label className="text-[11px]">Texto del botón "Sí" (ES)</Label>
                                <Input value={psc.boton_si || ""} placeholder="Ej. Sí, requiero este servicio" onChange={(e) => upd({ boton_si: e.target.value })} data-testid="proj-pago-boton-si" />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-[11px]">Texto del botón "No" (ES)</Label>
                                <Input value={psc.boton_no || ""} placeholder="Ej. No, no lo requiero" onChange={(e) => upd({ boton_no: e.target.value })} data-testid="proj-pago-boton-no" />
                              </div>
                              {projDialog.idiomas?.en && (
                                <>
                                  <div className="space-y-1">
                                    <Label className="text-[11px] text-blue-700">Botón "Sí" (EN)</Label>
                                    <Input value={psc.boton_si_en || ""} placeholder="Auto-completed with Translate button" onChange={(e) => upd({ boton_si_en: e.target.value })} data-testid="proj-pago-boton-si-en" />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-[11px] text-blue-700">Botón "No" (EN)</Label>
                                    <Input value={psc.boton_no_en || ""} placeholder="Auto-completed with Translate button" onChange={(e) => upd({ boton_no_en: e.target.value })} data-testid="proj-pago-boton-no-en" />
                                  </div>
                                </>
                              )}
                              {projDialog.idiomas?.fr && (
                                <>
                                  <div className="space-y-1">
                                    <Label className="text-[11px] text-blue-700">Bouton "Oui" (FR)</Label>
                                    <Input value={psc.boton_si_fr || ""} placeholder="Rempli avec le bouton Traduire" onChange={(e) => upd({ boton_si_fr: e.target.value })} data-testid="proj-pago-boton-si-fr" />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-[11px] text-blue-700">Bouton "Non" (FR)</Label>
                                    <Input value={psc.boton_no_fr || ""} placeholder="Rempli avec le bouton Traduire" onChange={(e) => upd({ boton_no_fr: e.target.value })} data-testid="proj-pago-boton-no-fr" />
                                  </div>
                                </>
                              )}
                            </div>

                            <div className="pt-2 border-t border-amber-100 space-y-2">
                              <div className="space-y-1">
                                <Label className="text-[11px] font-semibold">Mensaje al elegir "No" (ES)</Label>
                                <p className="text-[10px] text-slate-500">Este mensaje reemplaza la información del servicio cuando el viajero responde que no lo requiere. Déjalo vacío para simplemente ocultar el servicio.</p>
                                <RichTextEditor
                                  value={psc.texto_no || ""}
                                  onChange={(html) => upd({ texto_no: html })}
                                  placeholder="Ej. Gracias por confirmar. Continúa con el resto de tu registro."
                                  minHeight={110}
                                />
                              </div>
                              {projDialog.idiomas?.en && (
                                <div className="space-y-1">
                                  <Label className="text-[11px] text-blue-700 font-semibold">Message when "No" (EN)</Label>
                                  <RichTextEditor
                                    value={psc.texto_no_en || ""}
                                    onChange={(html) => upd({ texto_no_en: html })}
                                    placeholder="Auto-completed with Translate button"
                                    minHeight={90}
                                  />
                                </div>
                              )}
                              {projDialog.idiomas?.fr && (
                                <div className="space-y-1">
                                  <Label className="text-[11px] text-blue-700 font-semibold">Message quand "Non" (FR)</Label>
                                  <RichTextEditor
                                    value={psc.texto_no_fr || ""}
                                    onChange={(html) => upd({ texto_no_fr: html })}
                                    placeholder="Rempli avec le bouton Traduire"
                                    minHeight={90}
                                  />
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Vista previa */}
                          {servicios.length > 0 && servicios.some((s) => s.nombre) && psc.tiempo_contacto_valor > 0 && (
                            <div className="md:col-span-2 rounded-md bg-white border border-amber-300 p-3 text-xs text-slate-700">
                              <b className="text-amber-700">Vista previa (así lo verá el viajero):</b>
                              {servicios.length === 1 ? (
                                <p className="mt-1">
                                  El servicio <b>{servicios[0].nombre}</b> tiene un costo de <b>${Number(servicios[0].costo || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN</b>. Te contactaremos en un periodo no mayor a <b>{psc.tiempo_contacto_valor} {psc.tiempo_contacto_unidad}</b>.
                                </p>
                              ) : (
                                <>
                                  <p className="mt-1">Los servicios disponibles son:</p>
                                  <ul className="list-disc list-inside mt-1 space-y-0.5">
                                    {servicios.filter((s) => s.nombre).map((s, i) => (
                                      <li key={i}><b>{s.nombre}</b> — ${Number(s.costo || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN</li>
                                    ))}
                                  </ul>
                                  <p className="mt-1">El viajero puede elegir uno o más. Te contactaremos en <b>{psc.tiempo_contacto_valor} {psc.tiempo_contacto_unidad}</b>.</p>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="preguntas" className="pt-3">
                <FieldsConfigurator catalogo={catalogo} fc={projDialog.fields_config || {}} setFc={(fc) => setProjDialog({ ...projDialog, fields_config: fc })} />
              </TabsContent>
              <TabsContent value="diseno" className="pt-3">
                <DesignEditor projDialog={projDialog} setProjDialog={setProjDialog} />
              </TabsContent>
            </Tabs>
            <DialogFooter>
              <Button variant="outline" onClick={() => setProjDialog(null)}>Cancelar</Button>
              <Button onClick={saveProject} disabled={saving} className="bg-cyan-700 hover:bg-cyan-800" data-testid="save-project-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Guardar proyecto</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Detalle de perfil */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="perfil-detail-dialog">
          {detail && catalogo && (<>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 flex-wrap pr-8">
                <span>{detail.folio}</span>
                <Badge className={`${EST_CLS[sv("estatus")] || ""} border-0`}>{sv("estatus")}</Badge>
                <div className="ml-auto flex items-center gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportPerfil(detail.id, "xlsx")}
                    disabled={!!exportingPerfil}
                    className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 disabled:opacity-60"
                    data-testid="perfil-export-xlsx"
                    title="Descargar Excel"
                  >
                    {exportingPerfil === "xlsx" ? (
                      <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generando…</>
                    ) : (
                      <><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportPerfil(detail.id, "pdf")}
                    disabled={!!exportingPerfil}
                    className="h-8 border-red-300 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 disabled:opacity-60"
                    data-testid="perfil-export-pdf"
                    title="Descargar PDF"
                  >
                    {exportingPerfil === "pdf" ? (
                      <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generando…</>
                    ) : (
                      <><FileText className="w-4 h-4 mr-1.5" /> PDF</>
                    )}
                  </Button>
                  {canDeletePerfil && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => askDeletePerfil(detail)}
                      className="h-8 border-rose-300 text-rose-700 hover:bg-rose-600 hover:text-white hover:border-rose-600"
                      data-testid="perfil-eliminar"
                      title="Eliminar perfil (solo admin/supervisor)"
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" /> Eliminar
                    </Button>
                  )}
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="border border-cyan-200 bg-cyan-50/40 rounded-xl p-4 space-y-3">
              <p className="text-xs font-bold uppercase tracking-wide text-cyan-800">Campos de sistema</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div><Label className="text-xs">Folio</Label><Input value={detail.folio} disabled /></div>
                <div><Label className="text-xs">Registro</Label><Input value={(detail.registro_at || "").slice(0, 16).replace("T", " ")} disabled /></div>
                <div><Label className="text-xs">Última actualización</Label><Input value={(detail.ultima_actualizacion_at || "").slice(0, 16).replace("T", " ")} disabled /></div>
                <div><Label className="text-xs">Estatus</Label>
                  <Select value={sv("estatus")} onValueChange={(v) => setEdit("estatus", v)}>
                    <SelectTrigger data-testid="detalle-estatus"><SelectValue /></SelectTrigger>
                    <SelectContent>{ESTATUS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Tipo de viajero</Label>
                  <Select value={["Cliente PD Travel", "Cliente Tercero"].includes(sv("tipo_viajero")) ? sv("tipo_viajero") : "Cliente Tercero"} onValueChange={(v) => setEdit("tipo_viajero", v)}>
                    <SelectTrigger data-testid="detalle-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Cliente PD Travel">Cliente PD Travel</SelectItem><SelectItem value="Cliente Tercero">Cliente Tercero</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Registro en otros proyectos</Label>
                  {detail.otros_proyectos ? (
                    <button type="button" className="h-9 w-full border border-cyan-200 bg-cyan-50 rounded-md text-sm font-bold text-cyan-800 hover:bg-cyan-100 transition-colors"
                      onClick={() => { const f = { project_id: "", estatus: "", q: detail.datos?.email || "", desde: "", hasta: "" }; setFilters(f); loadPerfiles(f); setDetail(null); toast.info("Filtrando registros de este correo en todos los proyectos"); }}
                      data-testid="detalle-otros-proyectos">Sí — ver registros</button>
                  ) : <Input value="No" disabled data-testid="detalle-otros-proyectos" />}
                </div>
                <div><Label className="text-xs">Subtipo-cliente</Label><Input value={detail.subtipo_nombre || "General"} disabled data-testid="detalle-subtipo" /></div>
                <div><Label className="text-xs">Cliente asociado</Label><Input value={sv("cliente_asociado")} onChange={(e) => setEdit("cliente_asociado", e.target.value)} /></div>
                <div><Label className="text-xs">Empresa asociada</Label><Input value={sv("empresa_asociada")} onChange={(e) => setEdit("empresa_asociada", e.target.value)} /></div>
              </div>
            </div>
            {catalogo.sections.map((s) => (
              <div key={s.key} className="space-y-2">
                <p className="text-sm font-bold text-slate-700">{s.es}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {s.fields.map((f) => {
                    if (f.t === "file") {
                      const doc = (detail.documentos || []).find((d) => d.campo === f.k || d.tipo === f.k);
                      return (
                        <div key={f.k} className="md:col-span-2 flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2 text-sm">
                          <span className="text-slate-600 flex-1 min-w-0 truncate" title={doc?.filename}>{f.col}: {doc ? doc.filename : "—"}
                            {doc?.fecha_vencimiento && <Badge className={`ml-2 ${DOC_CLS[doc.estatus_doc] || ""} border-0`}>vence {doc.fecha_vencimiento}</Badge>}
                          </span>
                          <div className="flex items-center gap-1">
                            {doc && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setPreview({
                                    url: `${API}/viajeros-ext/perfiles/${detail.id}/archivo/${doc.id}?inline=1`,
                                    filename: doc.filename,
                                    label: f.col,
                                  })}
                                  data-testid={`ver-${f.k}`}
                                  title="Vista previa"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => downloadDoc(doc)} data-testid={`dl-${f.k}`} title="Descargar"><Download className="w-4 h-4" /></Button>
                              </>
                            )}
                            <input
                              type="file"
                              id={`up-${f.k}`}
                              className="hidden"
                              accept="image/*,application/pdf"
                              onChange={(e) => uploadDoc(f.k, e.target.files?.[0], e)}
                              data-testid={`up-input-${f.k}`}
                            />
                            <Button variant="outline" size="sm" onClick={() => document.getElementById(`up-${f.k}`).click()} data-testid={`up-${f.k}`}>
                              <Upload className="w-4 h-4 mr-1" /> {doc ? "Reemplazar" : "Subir"}
                            </Button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={f.k} className={f.t === "textarea" ? "md:col-span-2" : ""}>
                        <Label className="text-xs">{f.col}</Label>
                        {f.t === "select" ? (
                          <Select value={dv(f.k) || undefined} onValueChange={(v) => setEdit(f.k, v)} disabled={!canEditPerfil}>
                            <SelectTrigger data-testid={`edit-${f.k}`}><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>{(f.opts || []).map((o) => <SelectItem key={o.v} value={o.v}>{o.es || o.v}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : f.t === "check" ? (
                          <Select value={dv(f.k) || undefined} onValueChange={(v) => setEdit(f.k, v)} disabled={!canEditPerfil}>
                            <SelectTrigger data-testid={`edit-${f.k}`}><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent><SelectItem value="Sí">Sí</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
                          </Select>
                        ) : f.t === "textarea" ? (
                          <Textarea rows={2} value={dv(f.k)} onChange={(e) => setEdit(f.k, e.target.value)} data-testid={`edit-${f.k}`} disabled={!canEditPerfil} />
                        ) : (
                          <Input type={f.t === "date" ? "date" : f.t === "time" ? "time" : "text"} value={dv(f.k)} onChange={(e) => setEdit(f.k, e.target.value)} data-testid={`edit-${f.k}`} disabled={!canEditPerfil} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <div>
              <p className="text-sm font-bold text-slate-700 mb-2">Historial de cambios</p>
              <div className="space-y-2 max-h-60 overflow-y-auto pr-1" data-testid="log-auditoria">
                {(!detail.actualizado_por || detail.actualizado_por.length === 0) && (
                  <p className="text-xs text-slate-400 italic">Sin cambios registrados.</p>
                )}
                {(detail.actualizado_por || []).slice().reverse().map((l, i) => {
                  const cambios = Array.isArray(l.cambios) ? l.cambios : null;
                  const fmt = (v) => {
                    if (v === null || v === undefined || v === "") return "—";
                    if (v === true) return "Sí";
                    if (v === false) return "No";
                    const s = String(v);
                    return s.length > 120 ? s.slice(0, 120) + "…" : s;
                  };
                  return (
                    <div key={i} className="text-xs border-l-2 border-cyan-300 pl-2 py-0.5">
                      <p className="text-slate-500">
                        {(l.at || "").slice(0, 16).replace("T", " ")} · <b className="text-slate-700">{l.editor}</b>
                        {l.editor_role && <span className="ml-1 text-[10px] uppercase text-cyan-700 font-semibold">({l.editor_role})</span>}
                        <span className="ml-1 text-slate-400">· {l.via}</span>
                      </p>
                      {cambios ? (
                        <ul className="mt-1 space-y-0.5">
                          {cambios.map((c, k) => (
                            <li key={k} className="text-[11px] text-slate-600 flex items-baseline gap-1">
                              <span className="font-semibold text-slate-700 shrink-0">{c.label || c.campo}:</span>
                              <span className="line-through text-rose-600 truncate max-w-[220px]" title={String(c.de ?? "")}>"{fmt(c.de)}"</span>
                              <span className="text-slate-400">→</span>
                              <span className="text-emerald-700 font-semibold truncate max-w-[220px]" title={String(c.a ?? "")}>"{fmt(c.a)}"</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-[11px] text-slate-500">Campos: {(l.campos || []).join(", ") || "—"}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Toggle admin/supervisor: cambiar si el viajero requiere el servicio de pago */}
            {canEditPerfil && detail.pago_servicios && (
              <div className="border-2 border-amber-300 bg-amber-50/60 rounded-lg p-3">
                <p className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <CreditCard className="w-3.5 h-3.5" /> Pago de Servicios · edición admin
                </p>
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-slate-700">¿Requiere el servicio?</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: true, l: "Sí, requiere" },
                      { v: false, l: "No, ya no requiere" },
                    ].map((op) => {
                      const cur = edits.pago_requiere !== undefined ? edits.pago_requiere : detail.pago_servicios?.requiere;
                      const active = cur === op.v;
                      return (
                        <button
                          key={String(op.v)}
                          type="button"
                          onClick={() => setEdit("pago_requiere", op.v)}
                          className={`text-xs px-3 py-1.5 rounded-md border ${active ? "bg-amber-600 border-amber-600 text-white font-semibold" : "bg-white border-slate-300 text-slate-700 hover:border-amber-400"}`}
                          data-testid={`edit-pago-requiere-${op.v ? "si" : "no"}`}
                        >{op.l}</button>
                      );
                    })}
                  </div>
                </div>
                {edits.pago_requiere === false && detail.pago_servicios?.requiere === true && (
                  <p className="text-[11px] text-rose-700 mt-2">
                    ⚠️ Al guardar con <b>"No, ya no requiere"</b>, este perfil desaparecerá del tablero de <b>Pagos</b> y se limpiarán servicios seleccionados, total y medios de contacto.
                  </p>
                )}
              </div>
            )}
            {detail.pago_servicios?.requiere && (
              <PagoServiciosPanel
                ps={detail.pago_servicios}
                profileId={detail.id}
                users={usersList}
                registroAt={detail.registro_at}
                onChange={(newSeg) => {
                  // Actualiza detalle local + refresca perfiles list + fuerza reload de PagosTab
                  setDetail((prev) => prev ? { ...prev, pago_servicios: { ...prev.pago_servicios, seguimiento: newSeg } } : prev);
                  loadPerfiles();
                  setPagosRefreshKey((k) => k + 1);
                }}
              />
            )}
            <DialogFooter className="flex-wrap gap-2 sm:justify-end">
              <Button variant="outline" onClick={() => setDetail(null)}>Cerrar</Button>
              {canEditPerfil && (
                <Button onClick={savePerfil} disabled={saving || !Object.keys(edits).length} className="bg-cyan-700 hover:bg-cyan-800" data-testid="save-perfil-ext-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Guardar cambios {Object.keys(edits).length ? `(${Object.keys(edits).length})` : ""}
                </Button>
              )}
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Overlay bloqueante: generando documento */}
      {exportingPerfil && (
        <div
          className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
          data-testid="perfil-export-overlay"
        >
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 flex items-center gap-4 max-w-md mx-4 border border-slate-200">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${exportingPerfil === "pdf" ? "bg-red-50" : "bg-emerald-50"}`}>
              <Loader2 className={`w-6 h-6 animate-spin ${exportingPerfil === "pdf" ? "text-red-600" : "text-emerald-600"}`} />
            </div>
            <div>
              <p className="font-semibold text-slate-800 text-base">
                Generando {exportingPerfil === "pdf" ? "PDF" : "Excel"}…
              </p>
              <p className="text-sm text-slate-500 mt-0.5">Esto puede tardar unos segundos. Por favor no cierres esta ventana.</p>
            </div>
          </div>
        </div>
      )}

      {/* Confirmación de acciones de proyecto (admin) */}
      <AlertDialog open={!!projAction} onOpenChange={(o) => !o && setProjAction(null)}>
        <AlertDialogContent data-testid="proj-action-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {projAction?.tipo === "eliminar" ? `¿Eliminar el proyecto "${projAction?.p?.nombre}"?`
                : projAction?.tipo === "activo" ? `¿Reactivar el proyecto "${projAction?.p?.nombre}"?`
                : `¿${projAction?.tipo === "archivado" ? "Archivar" : "Cancelar"} el proyecto "${projAction?.p?.nombre}"?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {projAction?.tipo === "eliminar" ? "Esta acción no se puede deshacer."
                : projAction?.tipo === "activo" ? "El proyecto volverá a la vista de activos. El formulario público permanece cerrado hasta que lo actives en su configuración."
                : "El proyecto desaparecerá de la vista de activos pero sus registros se conservan. El formulario público se cerrará."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="proj-action-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction className={projAction?.tipo === "eliminar" || projAction?.tipo === "cancelado" ? "bg-red-600 hover:bg-red-700" : "bg-cyan-700 hover:bg-cyan-800"}
              onClick={() => { const a = projAction; setProjAction(null); a.tipo === "eliminar" ? deleteProject(a.p) : setProjectStatus(a.p, a.tipo); }}
              data-testid="proj-action-confirmar">
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DocumentPreviewDialog
        open={!!preview}
        onClose={() => setPreview(null)}
        url={preview?.url}
        filename={preview?.filename}
        label={preview?.label}
        testIdPrefix="viajero-ext-doc-preview"
      />

      {/* Confirmar eliminación de perfil (solo admin/supervisor) */}
      <Dialog open={!!deleteDialog} onOpenChange={(o) => !o && !deleting && setDeleteDialog(null)}>
        <DialogContent className="max-w-md" data-testid="perfil-eliminar-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="w-5 h-5" /> Eliminar perfil
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3 text-sm">
              <p className="text-rose-800"><b>{deleteDialog?.folio}</b> — {deleteDialog?.nombre || "Sin nombre"}</p>
              <p className="text-xs text-rose-700 mt-1">Esta acción quedará registrada en el log de auditoría con tu usuario, fecha y motivo. El respaldo del perfil se conserva en la base de datos.</p>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Motivo <span className="text-red-500">*</span></Label>
              <textarea
                value={deleteMotivo}
                onChange={(e) => setDeleteMotivo(e.target.value)}
                placeholder="Ej. Registro duplicado, viajero solicitó baja, prueba interna…"
                rows={3}
                maxLength={500}
                className="w-full mt-1 rounded-md border border-slate-300 p-2 text-sm focus:outline-none focus:border-rose-400"
                data-testid="perfil-eliminar-motivo"
              />
              <p className="text-[11px] text-slate-500 mt-1">{deleteMotivo.length}/500</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} disabled={deleting}>Cancelar</Button>
            <Button onClick={confirmDeletePerfil} disabled={deleting || !deleteMotivo.trim()} className="bg-rose-600 hover:bg-rose-700" data-testid="perfil-eliminar-confirmar">
              {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Eliminando…</> : <><Trash2 className="w-4 h-4 mr-2" /> Eliminar definitivamente</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Popup: Nuevo subtipo */}
      <Dialog open={subtipoNewOpen} onOpenChange={(o) => !o && setSubtipoNewOpen(false)}>
        <DialogContent className="max-w-md" data-testid="subtipo-new-dialog">
          <DialogHeader><DialogTitle>Nuevo subtipo-cliente</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-slate-600">Cada subtipo tiene su propio formulario público (preguntas, pago, mensaje) pero comparte el cliente y diseño del proyecto.</p>
            <div>
              <Label className="text-xs">Nombre del subtipo</Label>
              <Input value={subtipoNewName} onChange={(e) => setSubtipoNewName(e.target.value)} placeholder="Ej. STAFF, PROVEEDORES, PROMOTORES…" maxLength={60} data-testid="subtipo-new-nombre" onKeyDown={(e) => e.key === "Enter" && addSubtipo()} />
            </div>
            <p className="text-[10px] text-slate-500">Se copiarán las preguntas del subtipo <b>"{(projDialog?.subtipos?.[0]?.nombre) || "General"}"</b> como punto de partida. Puedes cambiarlas después.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSubtipoNewOpen(false); setSubtipoNewName(""); }}>Cancelar</Button>
            <Button onClick={addSubtipo} disabled={!subtipoNewName.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="subtipo-new-crear">Crear subtipo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Popup: Ver enlaces por subtipo */}
      <Dialog open={!!linksPopup} onOpenChange={(o) => !o && setLinksPopup(null)}>
        <DialogContent className="max-w-lg" data-testid="links-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ExternalLink className="w-4 h-4" /> Enlaces del proyecto</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 min-w-0 overflow-hidden">
            <p className="text-xs text-slate-600 mb-1">{linksPopup?.nombre} — <span className="text-slate-500">{(linksPopup?.subtipos || []).length} subtipo(s)</span></p>
            {(linksPopup?.subtipos || []).map((st) => {
              const url = `${window.location.origin}/viajero/${linksPopup.slug}/${st.slug}`;
              return (
                <div key={st.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 bg-slate-50/40 min-w-0">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="text-sm font-semibold text-slate-800 truncate">{st.nombre}</p>
                    <p className="text-[11px] text-slate-500 font-mono truncate whitespace-nowrap overflow-hidden text-ellipsis" title={url}>{url}</p>
                  </div>
                  <Button variant="outline" size="sm" className="h-8 shrink-0" onClick={() => { navigator.clipboard.writeText(url); toast.success("Copiado"); }} data-testid={`copy-link-${st.id}`}><Copy className="w-3.5 h-3.5" /></Button>
                  <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center h-8 px-2 rounded-md border border-slate-300 hover:bg-slate-100 shrink-0" title="Abrir en nueva pestaña" data-testid={`open-link-${st.id}`}><ExternalLink className="w-3.5 h-3.5" /></a>
                </div>
              );
            })}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setLinksPopup(null)}>Cerrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Popup: Wizard nuevo proyecto */}
      <Dialog open={!!newProjWizard} onOpenChange={(o) => !o && setNewProjWizard(null)}>
        <DialogContent className="max-w-md" data-testid="new-proj-wizard">
          <DialogHeader><DialogTitle>Nuevo proyecto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600">Cada proyecto tiene 1 o más subtipos-cliente (STAFF, PROVEEDORES, etc). El primero se crea ahora y puedes agregar más después.</p>
            <div>
              <Label className="text-xs">Nombre del proyecto *</Label>
              <Input value={newProjWizard?.nombre || ""} onChange={(e) => setNewProjWizard((w) => ({ ...w, nombre: e.target.value }))} placeholder="Ej. JLR Summit 2026" maxLength={120} data-testid="new-proj-nombre" autoFocus />
            </div>
            <div>
              <Label className="text-xs">Nombre del primer subtipo-cliente *</Label>
              <Input value={newProjWizard?.subtipo || ""} onChange={(e) => setNewProjWizard((w) => ({ ...w, subtipo: e.target.value }))} placeholder="Ej. General, STAFF, INVITADOS…" maxLength={60} data-testid="new-proj-subtipo" onKeyDown={(e) => e.key === "Enter" && startNewProjectFromWizard()} />
              <p className="text-[10px] text-slate-500 mt-1">Se llena como "General" por defecto. Podrás cambiarlo o agregar más subtipos después.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewProjWizard(null)}>Cancelar</Button>
            <Button onClick={startNewProjectFromWizard} className="bg-cyan-700 hover:bg-cyan-800" data-testid="new-proj-continuar">Continuar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
